// src/pages/BootcampCertified.jsx
import React, { useEffect, useMemo, useRef, useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import {
 PlayCircleIcon, CheckCircleIcon, ClockIcon, TrophyIcon, ClipboardDocumentListIcon,
 LockClosedIcon, BookOpenIcon, XMarkIcon, DocumentTextIcon, ShieldCheckIcon,
 ChatBubbleLeftRightIcon, PaperAirplaneIcon, PaperClipIcon, ArrowPathIcon,
 ExclamationTriangleIcon, PhotoIcon, VideoCameraIcon, ArrowTopRightOnSquareIcon,
 ArrowDownTrayIcon,
 SpeakerWaveIcon,
 ArrowRightIcon,
 SparklesIcon,
 CheckCircleIcon as CheckCircleIconSolid
} from "@heroicons/react/24/outline";
import { FaGithub as GitHubIcon } from "react-icons/fa";
import API_BASE_URL from "../Config/config";
import { pub } from "../utils/pub";
import SubscriptionStatus from './SubscriptionStatus';
import VoiceAssistant from './NewVoiceAssistant';
import { useVoiceAssistant } from '../hooks/useVoiceAssistant';
import ThemeToggle from './ThemeToggle';
import { useTaskCompletion } from '../hooks/useTaskCompletion';
import SimplePracticeCodingButton from './SimplePracticeCodingButton';
import { updateLearningConsistency } from "../utils/consistency";
import AchievementSystem from "./AchievementSystem";
import AIMentorController from "./AIMentorController";
import { UserContext } from "../BasicComponents/UserContext";
// TSGLanguageSelector removed - always English
import { checkLLMTrialStatus } from '../utils/llmTrialManager';
import { hasFullAccess, redirectToPaymentIfNeeded } from '../utils/paymentStatus';
import VisualizationPreview from "./shared/VisualizationPreview";

/* ---------------------------------- utils --------------------------------- */
const TabButton = ({ active, onClick, children }) => (
  <button 
    onClick={onClick} 
    className={`px-4 py-2 rounded-xl text-sm font-bold transition-all border ${
      active 
        ? "bg-primary text-white border-primary shadow-lg shadow-primary/25" 
        : "bg-white text-muted border-gray-200 hover:border-primary/50 hover:text-primary"
    }`}
  >
    {children}
  </button>
);
const Button = ({ children, onClick, className = "", variant = "primary", ...props }) => {
  const baseStyles = "px-6 py-3 rounded-xl font-bold transition-all flex items-center justify-center gap-2";
  const variants = {
    primary: "bg-primary text-white hover:bg-primary/90 shadow-lg shadow-primary/25",
    outline: "border-2 border-primary text-primary hover:bg-primary/5",
    secondary: "bg-secondary/10 text-primary hover:bg-secondary/20"
  };
  return (
    <button onClick={onClick} className={`${baseStyles} ${variants[variant]} ${className}`} {...props}>
      {children}
    </button>
  );
};
const Field = ({ label, children }) => (<div><div className="text-sm text-muted">{label}</div>{children}</div>);
const Info = ({ label, children }) => (<div className="rounded-xl border border-secondary/20 bg-bgMain/40 p-3"><div className="text-xs text-muted mb-1">{label}</div><p className="text-sm text-gray-700 whitespace-pre-line">{children}</p></div>);

const isDark = false;
const clamp = (n, lo, hi) => Math.max(lo, Math.min(hi, n));
const slugify = (s ="") =>
 s.toString().trim().toLowerCase()
 .replace(/&/g," and").replace(/\+/g," plus")
 .replace(/[^a-z0-9]+/g,"-").replace(/(^-|-$)+/g,"");

// Safely convert explanation values to strings (handles objects, arrays, etc.)
const safeToString = (value) => {
  if (value == null) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (Array.isArray(value)) return value.map(safeToString).join('. ');
  if (typeof value === 'object') {
    if (value.topicOverview) return safeToString(value.topicOverview);
    if (value.explanation) return safeToString(value.explanation);
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }
  return String(value);
};

// Convert CloudFront/S3 URLs to proxy URLs to avoid CORS issues
const getProxiedVideoUrl = (url) => {
 if (!url) return url;
 
 // Check if URL is from CloudFront or S3
 const cloudfrontPattern = /https?:\/\/d1atm4dw3zgpab\.cloudfront\.net\//;
 const s3Pattern = /https?:\/\/.*\.s3\.(.*\.)?amazonaws\.com\//;
 
 if (cloudfrontPattern.test(url) || s3Pattern.test(url)) {
 // Use proxy endpoint to add CORS headers
 const encodedUrl = encodeURIComponent(url);
 return `${API_BASE_URL}/api/video/proxy?url=${encodedUrl}`;
 }
 
 return url;
};

const PUBLIC_ROOT ="certified";

async function fetchUserContext(userId) {
  const sessionToken = localStorage.getItem('session_token');
  const res = await fetch(`${API_BASE_URL}/api/bootcamp/certified/user-context/${encodeURIComponent(userId)}?userId=${encodeURIComponent(userId)}`, {
   headers: {
     'Authorization': sessionToken ? `Bearer ${sessionToken}` : '',
     'X-Session-Token': sessionToken || ''
   }
  });
  const data = await res.json();
  if (!res.ok || !data.success) throw new Error(data.error || 'Failed to fetch user context');
  return data.data; // Already contains { user, enrollment }
}
const LEVEL_FOLDERS = ["0-2-years","3-6-years","6-9-years","10-12-years","13-15-years","15-plus-years"];

function toLevelFolder(level) {
 const raw = String(level ||"").toLowerCase()
 .replace(/[–—]/g,"-").replace(/\byrs?\b/g,"years")
 .replace(/\by\.?o\.e\.?\b/g,"years").replace(/\s+/g,"").trim();
 if (/(^|[^0-9])15\s*\+/.test(raw) || /15\+/.test(raw) || /15\s*(or\s*more|plus)/.test(raw)) return"15-plus-years";
 if (/^0\s*-\s*2|^0\s*to\s*2|^0\s*2/.test(raw)) return"0-2-years";
 if (/^3\s*-\s*6|^3\s*to\s*6/.test(raw)) return"3-6-years";
 if (/^6\s*-\s*9|^6\s*to\s*9/.test(raw)) return"6-9-years";
 if (/^10\s*-\s*12|^10\s*to\s*12/.test(raw)) return"10-12-years";
 if (/^13\s*-\s*15|^13\s*to\s*15/.test(raw)) return"13-15-years";
 const hyphened = raw.replace(/\s+/g,"-");
 if (LEVEL_FOLDERS.includes(hyphened)) return hyphened;
 throw new Error(`Unsupported experience level:"${level}". Expected one of ${LEVEL_FOLDERS.join(",")}`);
}

/* --------------------------- local progress storage ------------------------ */
const pKey = (userId, tag) => `progress_${userId}_${slugify(tag)}`;
const readProgress = (userId, tag) => {
 try {
 const raw = localStorage.getItem(pKey(userId, tag));
 if (!raw) return { completedTaskIds: [], reviewStateById: {} };
 const parsed = JSON.parse(raw);
 if (Array.isArray(parsed)) return { completedTaskIds: parsed, reviewStateById: {} };
 return {
 completedTaskIds: parsed.completedTaskIds || [],
 reviewStateById: parsed.reviewStateById || {},
 };
 } catch { return { completedTaskIds: [], reviewStateById: {} }; }
};
const writeProgress = (userId, tag, obj) => {
  try {
    localStorage.setItem(pKey(userId, tag), JSON.stringify(obj));
    // Dispatch event to notify other components (like CertificatePage) that progress has changed
    window.dispatchEvent(new CustomEvent('skillgenProgressUpdated', { detail: { userId, tag } }));
  } catch {}
};

/* ------------------------------ mentor chat LS ----------------------------- */
const mKey = (userId, tag) => `mentor_${userId}_${slugify(tag)}`;
const readChat = (userId, tag) => { try { return JSON.parse(localStorage.getItem(mKey(userId, tag)) ||"[]"); } catch { return []; } };
const writeChat = (userId, tag, msgs) => { try { localStorage.setItem(mKey(userId, tag), JSON.stringify(msgs)); } catch {} };

/* ------------------------------ AI Evidence Detection ----------------------------- */
function determineEvidenceType(taskTopic, taskTitle = "") {
  const topic = (taskTopic || taskTitle || "").toLowerCase();

  // Installation/Setup tasks → Screenshot + Terminal
  if (topic.includes("setup") || topic.includes("install") || topic.includes("environment") ||
      topic.includes("ide") || topic.includes("jdk") || topic.includes("configuration")) {
    return {
      type: "installation",
      methods: ["screenshot", "terminal"],
      requiresGithub: false,
      aiGuidance: "For setup and installation tasks, please provide a screenshot of your successful installation or terminal output showing the version/configuration."
    };
  }

  // Git/GitHub tasks → GitHub repo review
  if (topic.includes("git") || topic.includes("github") || topic.includes("version control") ||
      topic.includes("commit") || topic.includes("repository")) {
    return {
      type: "git",
      methods: ["github"],
      requiresGithub: true,
      aiGuidance: "For Git tasks, I'll review your commits and repository structure automatically."
    };
  }

  // Theory/Concept tasks → AI Questions
  if (topic.includes("introduction") || topic.includes("theory") || topic.includes("concept") ||
      topic.includes("overview") || topic.includes("fundamentals") || topic.includes("basics")) {
    return {
      type: "theory",
      methods: ["questions"],
      requiresGithub: false,
      aiGuidance: "Let's discuss the concepts. I'll ask you questions to verify your understanding."
    };
  }

  // Small coding tasks → Built-in compiler or files
  if (topic.includes("variables") || topic.includes("loops") || topic.includes("operators") ||
      topic.includes("arrays") || topic.includes("strings") || topic.includes("functions")) {
    return {
      type: "small_code",
      methods: ["compiler", "files"],
      requiresGithub: false,
      aiGuidance: "You can use the built-in compiler or upload your code file for review."
    };
  }

  // Medium/Large coding tasks → GitHub
  if (topic.includes("project") || topic.includes("assignment") || topic.includes("application") ||
      topic.includes("system") || topic.includes("framework") || topic.includes("api") ||
      topic.includes("database") || topic.includes("spring") || topic.includes("hibernate")) {
    return {
      type: "project",
      methods: ["github"],
      requiresGithub: true,
      aiGuidance: "Push your code to GitHub. I'll review your implementation, code quality, and best practices."
    };
  }

  // Default → Allow both methods
  return {
    type: "general",
    methods: ["files", "github"],
    requiresGithub: false,
    aiGuidance: "Submit your work via files or GitHub repository."
  };
}

/* ------------------------------ backend calls ------------------------------ */
async function fetchCertifiedUserContext(userId) {
  const sessionToken = localStorage.getItem('session_token');
  const headers = {
    'Content-Type': 'application/json',
    
    
  };
  if (sessionToken) {
    headers['Authorization'] = `Bearer ${sessionToken}`;
    headers['X-Session-Token'] = sessionToken;
  }
  const res = await fetch(`${API_BASE_URL}/api/bootcamp/certified/user-context/${encodeURIComponent(userId)}?userId=${encodeURIComponent(userId)}`, {
    headers
  });
  const data = await res.json();
  if (!res.ok || !data.ok) throw new Error(data.error || `HTTP ${res.status}`);
  return data;
}
async function reviewTaskWithAI({ userId, courseName, experienceLevel, task, docFile, imgFile, comments, githubUrl, submissionType }) {
  const url = `${API_BASE_URL}/api/user-ai-review`;
  const sessionToken = localStorage.getItem('session_token');
  const fd = new FormData();
  fd.append("userId", userId);
  fd.append("bunchName", courseName);
  fd.append("experienceLevel", experienceLevel);
  fd.append("taskId", task.id);
  fd.append("taskTitle", task.title);
  fd.append("expectedOutput", task.expectedOutput ||"");
  fd.append("useCase", task.useCase ||"");
  fd.append("futureUseCase", task.futureUseCase ||"");
  fd.append("subtopics", JSON.stringify(task.subtopics || []));
  fd.append("submissionType", submissionType ||"files");
  fd.append("githubUrl", githubUrl ||"");
  fd.append("comments", comments ||"");
  if (docFile) fd.append("docFile", docFile);
  if (imgFile) fd.append("imgFile", imgFile);

  const headers = {};
  if (sessionToken) {
    headers['Authorization'] = `Bearer ${sessionToken}`;
    headers['X-Session-Token'] = sessionToken;
  }

  const res = await fetch(`${url}?userId=${encodeURIComponent(userId)}`, { method:"POST", body: fd, headers });
 const data = await res.json();
 if (!res.ok || !data.ok) throw new Error(data.error || `HTTP ${res.status}`);
 return data;
}
async function askMentorAPI({ userId, courseName, experienceLevel, task, text, imgFile, history = [] }) {
  const url = `${API_BASE_URL}/api/bootcamp/certified/mentor`;
  const sessionToken = localStorage.getItem('session_token');
  const fd = new FormData();
  fd.append("userId", userId);
  fd.append("courseName", courseName);
  fd.append("experienceLevel", experienceLevel);
  fd.append("taskId", task?.id ||"");
  fd.append("taskTitle", task?.title || task?.topic ||"");
  fd.append("context", JSON.stringify({
  expectedOutput: task?.expectedOutput ||"",
  subtopics: task?.subtopics || [],
  description: task?.description ||""
  }));
  fd.append("message", text ||"");
  fd.append("history", JSON.stringify(history.slice(-10).map(m => ({ role: m.role, text: m.text })))); // keep small
  fd.append("courseName", courseName);
  if (imgFile) fd.append("image", imgFile);

  const headers = {
    'Content-Type': 'application/json',
    
    
  };
  if (sessionToken) {
    headers['Authorization'] = `Bearer ${sessionToken}`;
    headers['X-Session-Token'] = sessionToken;
  }

  const res = await fetch(`${url}?userId=${encodeURIComponent(userId)}`, { method:"POST", body: fd, headers });
  const data = await res.json();
  if (!res.ok || !data.ok) throw new Error(data.error || `HTTP ${res.status}`);
  return data;
}
async function visualizeOutputAPI({ userId, courseName, experienceLevel, task, hint }) {
  const url = `${API_BASE_URL}/api/bootcamp/certified/visualize`;
  const sessionToken = localStorage.getItem('session_token');
  
  // Add timeout to prevent hanging requests
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout
  
  try {
    const headers = { 
      "Content-Type": "application/json",
      'Content-Type': 'application/json'
      
       || ""
    };
    if (sessionToken) {
      headers['Authorization'] = `Bearer ${sessionToken}`;
      headers['X-Session-Token'] = sessionToken;
    }
    const res = await fetch(`${url}?userId=${encodeURIComponent(userId)}`, {
      method:"POST",
      headers,
      body: JSON.stringify({
        userId, courseName, experienceLevel,
        taskId: task?.id ||"", taskTitle: task?.title || task?.topic ||"",
        expectedOutput: task?.expectedOutput ||"",
        subtopics: task?.subtopics || [],
        hint: hint ||"",
        visualization: task?.visualization || null, // pass through from JSON, if present
        taskDescription: task?.description || "",
        taskSubtopics: task?.subtopics || [],
        taskUseCase: task?.useCase || "",
      }),
      signal: controller.signal,
    });
 
 clearTimeout(timeoutId);

 // Handle 504 Gateway Timeout specifically
 if (res.status === 504) {
 throw new Error("The visualization is taking longer than expected. This might be due to high server load. Please try again in a moment.");
 }

 // Check if response is HTML (error page) instead of JSON
 const contentType = res.headers.get("content-type");
 if (!contentType || !contentType.includes("application/json")) {
 const text = await res.text();
 throw new Error(`Server returned HTML instead of JSON. Status: ${res.status}. Response: ${text.substring(0, 200)}...`);
 }

 const data = await res.json();
 if (!res.ok || !data.ok) {
 // Provide more helpful error messages
 if (res.status === 504) {
 throw new Error("The visualization request timed out. Please try again with a simpler request or try again later.");
 }
 throw new Error(data.error || `HTTP ${res.status}`);
 }
 
 // Ensure absolute URL for the generated image
 const imageUrl = (data.imageUrl || data.visualUrl ||"").startsWith("http")
 ? (data.imageUrl || data.visualUrl)
 : `${API_BASE_URL}${data.imageUrl || data.visualUrl ||""}`;
 return { ...data, imageUrl };
 } catch (err) {
 clearTimeout(timeoutId);
 // Handle abort (timeout) errors
 if (err.name === 'AbortError') {
 throw new Error("Request timed out. The visualization is taking too long. Please try again.");
 }
 // Re-throw other errors
 throw err;
 }
}
async function generateTopicExplanationAPI({ userId, courseName, experienceLevel, task, language = 'en' }) {
  const url = `${API_BASE_URL}/api/bootcamp/certified/explain-topic`;
  const sessionToken = localStorage.getItem('session_token');
  
  // Add timeout to prevent hanging requests
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout
  
  try {
    const headers = { 
      "Content-Type": "application/json",
      'Content-Type': 'application/json'
      
       || ""
    };
    if (sessionToken) {
      headers['Authorization'] = `Bearer ${sessionToken}`;
      headers['X-Session-Token'] = sessionToken;
    }
    const res = await fetch(`${url}?userId=${encodeURIComponent(userId)}`, {
      method:"POST",
      headers,
 body: JSON.stringify({
 userId, courseName, experienceLevel, task, language,
 taskDescription: task?.description || "",
 taskSubtopics: task?.subtopics || [],
 taskUseCase: task?.useCase || "",
 taskFutureUseCase: task?.futureUseCase || "",
 taskExpectedOutput: task?.expectedOutput || "",
 }),
 signal: controller.signal,
 });
 
 clearTimeout(timeoutId);

 // Handle 504 Gateway Timeout specifically
 if (res.status === 504) {
 throw new Error("The explanation is taking longer than expected. This might be due to high server load. Please try again in a moment.");
 }

 // Check if response is HTML (error page) instead of JSON
 const contentType = res.headers.get("content-type");
 if (!contentType || !contentType.includes("application/json")) {
 const text = await res.text();
 throw new Error(`Server returned HTML instead of JSON. Status: ${res.status}. Response: ${text.substring(0, 200)}...`);
 }

 const data = await res.json();
 if (!res.ok || !data.ok) {
 // Provide more helpful error messages
 if (res.status === 504) {
 throw new Error("The explanation request timed out. Please try again with a simpler topic or try again later.");
 }
 throw new Error(data.error || `HTTP ${res.status}`);
 }
 return data;
 } catch (err) {
 clearTimeout(timeoutId);
 // Handle abort (timeout) errors
 if (err.name === 'AbortError') {
 throw new Error("Request timed out. The explanation is taking too long. Please try again.");
 }
 // Re-throw other errors
 throw err;
 }
}

async function generateVideoAPI({ userId, task, courseType, experienceLevel }) {
  const url = `${API_BASE_URL}/api/video/generate`;
  const sessionToken = localStorage.getItem('session_token');
  const headers = { 
    "Content-Type": "application/json",
    'Content-Type': 'application/json'
    
     || ""
  };
  if (sessionToken) {
    headers['Authorization'] = `Bearer ${sessionToken}`;
    headers['X-Session-Token'] = sessionToken;
  }
  const res = await fetch(`${url}?userId=${encodeURIComponent(userId)}`, {
    method:"POST",
    headers,
 body: JSON.stringify({
 userId, task, courseType, experienceLevel, duration: 75
 }),
 });

 // Check if response is HTML (error page) instead of JSON
 const contentType = res.headers.get("content-type");
 if (!contentType || !contentType.includes("application/json")) {
 const text = await res.text();
 throw new Error(`Server returned HTML instead of JSON. Status: ${res.status}. Response: ${text.substring(0, 200)}...`);
 }

 const data = await res.json();
 if (!res.ok || !data.ok) throw new Error(data.error || `HTTP ${res.status}`);
 return data;
}

/* --------------------------- robust JSON loader ---------------------------- */
function mapTasks(tag, data) {
 let items = [];
 if (Array.isArray(data?.tasks)) items = data.tasks;
 else if (Array.isArray(data?.topics)) items = data.topics;
 else if (data && (data.topic || data.task_title)) items = [data];

 return items.map((it, i) => ({
 id: it.task_id || `${slugify(tag)}-${slugify(it.topic || it.task_title || `topic-${i + 1}`)}`,
 title: it.task_title || `Task: ${it.topic || `Topic ${i + 1}`}`,
 topic: it.topic || it.task_title || `Topic ${i + 1}`,
 description: it.task_description || it.description ||"",
 subtopics: Array.isArray(it.subtopics) ? it.subtopics : [],
 useCase: it.use_case || it.useCase ||"",
 futureUseCase: it.future_use_case || it.futureUseCase ||"",
 expectedOutput: it.expected_output || it.expectedOutput ||"",
 externalTools: it.external_tools || it.externalTools || [],
 visualization: it.visualization || null,
 }));
}
async function fetchJsonSafe(url) {
 const res = await fetch(url, { cache:"no-store" });
 const text = await res.text();
 const looksHtml = /^\s*<!doctype html>|^\s*<html[\s>]/i.test(text);
 try {
 const json = JSON.parse(text);
 return { ok: res.ok, json, raw: text, ct: res.headers.get("content-type") ||"" };
 } catch {
 if (looksHtml) {
 throw new Error(
 `Tasks file exists but the dev server returned HTML (likely a wrong path).\n` +
 `Check that the file is at /public/${PUBLIC_ROOT}/tasks/... and that the URL is correct.\n\nURL tried: ${url}`
 );
 }
 throw new Error("File exists but is not valid JSON and could not be auto-fixed.");
 }
}
async function loadTasks(tag, level) {
 const lvl = toLevelFolder(level);
 const url = pub(`/${PUBLIC_ROOT}/tasks/${slugify(tag)}/${encodeURIComponent(lvl)}.json`);
 
 // Check if user is paid
 const storedUser = localStorage.getItem("user");
 let userId = null;
 try {
   const parsedUser = storedUser ? JSON.parse(storedUser) : null;
   userId = parsedUser?.userId || parsedUser?.id || parsedUser?.user_id || localStorage.getItem("user_id");
 } catch (err) {
   userId = localStorage.getItem("user_id");
 }

 const user = JSON.parse(localStorage.getItem("user") || "{}");
 const isMainPlatform = window.location.hostname === 'www.theskillgenie.com' || 
                        window.location.hostname === 'localhost' ||
                        window.location.hostname === '127.0.0.1';
 
 const isStudentTenant = user && (user.role === 'student' && (user.tenant || user.college_id || !isMainPlatform));
 let isPaid = isStudentTenant;
 
 if (!isPaid && userId) {
    const { hasFullAccess } = await import('../utils/paymentStatus');
    isPaid = await hasFullAccess(userId);
 }

 const { json } = await fetchJsonSafe(url);
 const allTasks = mapTasks(tag, json);

  // If not paid, don't allow access (no free trial for bootcamp)
  if (!isPaid) {
     return [];
  }

 return allTasks;
}
async function loadResources(tag, level) {
 const lvl = toLevelFolder(level);
 const url = pub(`/${PUBLIC_ROOT}/tasks/${slugify(tag)}/${encodeURIComponent(lvl)}.json`);
 try {
 const { json } = await fetchJsonSafe(url);
 return json?.resourcesByTaskId && typeof json.resourcesByTaskId ==="object" ? json.resourcesByTaskId : {};
 } catch { return {}; }
}

/* -------------------------------- component -------------------------------- */

export default function BootcampCertified() {
  const { user } = useContext(UserContext);
  const userIdFromContext = user?.uid || user?.id || localStorage.getItem('userId');

  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [notAllowed, setNotAllowed] = useState("");
  const [userId, setUserId] = useState(null);
  const [enrollment, setEnrollment] = useState(null);

  const [tasks, setTasks] = useState([]);
  const [completedTaskIds, setCompletedTaskIds] = useState([]);
  const [viewIndex, setViewIndex] = useState(0);

  // Derived state (Moved to top to prevent ReferenceError)
  const totalTasks = tasks.length;
  const doneSet = useMemo(() => new Set(completedTaskIds), [completedTaskIds]);
  const completedCount = useMemo(() => {
    let c = 0;
    for (let i = 0; i < totalTasks; i++) {
      const id = tasks[i]?.id;
      if (id && doneSet.has(id)) c++;
      else break;
    }
    return c;
  }, [doneSet, tasks, totalTasks, completedTaskIds]);

  const unlockedIndex = clamp(completedCount, 0, Math.max(0, totalTasks - 1));
  const viewingTask = viewIndex != null ? tasks[viewIndex] : null;

  const [visualUrl, setVisualUrl] = useState("");
  const openTask = (idx, tab ="overview") => { setViewIndex(idx); setModalTab(tab); setShowModal(true); setVisualUrl(""); };
  const openCurrentTask = (tab ="overview") => openTask(unlockedIndex, tab);

  useEffect(() => {
    if (userIdFromContext) {
      updateLearningConsistency(userIdFromContext, 'OPEN_DASHBOARD', 'Bootcamp');
    }
  }, [userIdFromContext]);

  const [userName, setUserName] = useState("");
  const [track, setTrack] = useState("");
  const [courseName, setCourseName] = useState("");
  const technology = courseName || "Certification";
  const [experienceLevel, setExperienceLevel] = useState("");
  const [githubRepoUrl, setGithubRepoUrl] = useState("");
  const [invitationStatus, setInvitationStatus] = useState("");
  const [githubStatus, setGithubStatus] = useState(null);

  const syncInvitationStatus = async () => {
    if (!userId) return;
    try {
      const ctx = await fetchUserContext(userId);
      const enr = ctx?.enrollment;
      if (enr) {
        setInvitationStatus(enr.invitationStatus || enr.invitation_status || "");
        setGithubRepoUrl(enr.githubRepoUrl || enr.github_repo_url || "");
        setGithubStatus({
          repo_url: enr.githubRepoUrl || enr.github_repo_url || "",
          invitation_status: enr.invitationStatus || enr.invitation_status || "",
          provisioning_status: enr.provisioningStatus || enr.provisioning_status || (enr.githubRepoUrl || enr.github_repo_url ? "READY" : "PENDING")
        });
      }
    } catch (err) {
      console.error("Failed to sync invitation status", err);
    }
  };

  const [resourcesByTaskId, setResourcesByTaskId] = useState({});
  const [reviewStateById, setReviewStateById] = useState({});
  
  // Task completion tracking
  const { markTaskCompleted } = useTaskCompletion(userId, 'certified');

  const [modalTab, setModalTab] = useState("overview");
 const [showModal, setShowModal] = useState(false);
 const [showMentor, setShowMentor] = useState(false);
 const [mentorMessages, setMentorMessages] = useState([]);
 
 // 🎯 LLM Trial state
 const [llmTrialStatus, setLlmTrialStatus] = useState(null);
 const [showTrialModal, setShowTrialModal] = useState(false);
 const [showTrialExpiredModal, setShowTrialExpiredModal] = useState(false);

 // Voice Assistant
 const [vaInitialMessage, setVaInitialMessage] = useState("");
 const {
   isVoiceAssistantOpen,
   openVoiceAssistant,
   closeVoiceAssistant,
   handleVoiceCommand,
   handleVoiceNavigation
 } = useVoiceAssistant();

 // Helper to open Voice Assistant with subtopic explanation
 const explainWithAI = async (subtopic) => {
   try {
     setVaInitialMessage(`Generating explanation for: ${subtopic}...`);
     openVoiceAssistant();
     
     // Fetch detailed explanation for the subtopic
     const res = await fetch(`${API_BASE_URL}/chat`, {
       method: 'POST',
       headers: {
         'Content-Type': 'application/json',
         'user-id': userId
       },
       body: JSON.stringify({
         message: `Explain the subtopic "${subtopic}" for the topic "${tasks[viewIndex]?.title || tasks[viewIndex]?.topic}" in ${technology}. Provide a detailed explanation that I can read.`,
         langMode: "fixed",
         langCode: "en-US",
         userId: userId
       })
     });
     
     if (res.ok) {
       const data = await res.json();
       setVaInitialMessage(data.reply);
     } else {
       setVaInitialMessage(`Sorry, I couldn't generate an explanation for "${subtopic}" right now. Please try again.`);
     }
   } catch (err) {
     console.error("Failed to explain subtopic:", err);
     setVaInitialMessage("Error connecting to AI Assistant.");
   }
 };

 const [visualizing, setVisualizing] = useState(false);
 const [visualGeneratedAt, setVisualGeneratedAt] = useState(null);
 const [visualHint, setVisualHint] = useState("");

 const [explanationLoading, setExplanationLoading] = useState(false);
 const [topicExplanation, setTopicExplanation] = useState(null);
 const [explanationLanguage] = useState('en'); 
 const [explanationUsage, setExplanationUsage] = useState(null);
 const [videoUsage, setVideoUsage] = useState(null);
 const [videoGenerating, setVideoGenerating] = useState(false);
 const [videoUrl, setVideoUrl] = useState(null);
 const [videoStatus, setVideoStatus] = useState(""); // generating | queued | ready | error
 const [videoMessage, setVideoMessage] = useState("");
 const [videoError, setVideoError] = useState("");
 const videoPollRef = useRef(null);
 const videoBaseUrlRef = useRef(null);
 const videoPollInFlightRef = useRef(false);
 const videoStatusRef = useRef(""); // Track video status in ref to avoid closure issues
 const videoPollStartedAtRef = useRef(null);
 const videoErrorRetriesRef = useRef(0);
 const [selectedLanguage] = useState('en'); // Always English - language selection removed

 useEffect(() => {
 const id =
 localStorage.getItem("user_id") ||
 (() => {
 try {
 const u = JSON.parse(localStorage.getItem("user") ||"{}");
 return u.userId || u.user_id ||"";
 } catch { return""; }
 })();
 setUserId(id ||"");
 }, []);

 // Check payment status before loading dashboard
 useEffect(() => {
 let alive = true;
 (async () => {
 if (userId === null || !userId) return;
 
 try {
 const hasAccess = await hasFullAccess(userId);
 if (!hasAccess && alive) {
 const proceed = await redirectToPaymentIfNeeded(userId, navigate);
 if (!proceed && alive) {
 setLoading(false);
 setNotAllowed("Premium subscription required to access this dashboard.");
 return;
 }
 }
 } catch (err) {
 console.error("Payment check failed:", err);
 // Continue loading - backend will also check
 }
 })();
 return () => { alive = false; };
 }, [userId, navigate]);

 const [githubUrl, setGithubUrl] = useState("");
 const [submissionType, setSubmissionType] = useState("files");

 useEffect(() => {
 let alive = true;
 (async () => {
 try {
 if (userId === null) return;
 if (!userId) { if (alive) { setLoading(false); setNotAllowed("Missing login."); } return; }

 const ctx = await fetchCertifiedUserContext(userId);
 if (alive) {
 setUserName(ctx.user?.name || userId);
 setTrack(ctx.enrollment?.track ||"certified");
  setCourseName(ctx.enrollment?.courseName || "");
  setEnrollment(ctx.enrollment);
  setExperienceLevel(ctx.enrollment?.experienceLevel || "");
  setGithubRepoUrl(ctx.enrollment?.githubRepoUrl || ctx.enrollment?.github_repo_url || "");
  setInvitationStatus(ctx.enrollment?.invitationStatus || ctx.enrollment?.invitation_status || "");
  setGithubStatus({
    repo_url: ctx.enrollment?.githubRepoUrl || ctx.enrollment?.github_repo_url || "",
    invitation_status: ctx.enrollment?.invitationStatus || ctx.enrollment?.invitation_status || "",
    provisioning_status: ctx.enrollment?.provisioningStatus || ctx.enrollment?.provisioning_status || (ctx.enrollment?.githubRepoUrl || ctx.enrollment?.github_repo_url ? "READY" : "PENDING")
  });
  if (ctx.enrollment?.githubRepoUrl || ctx.enrollment?.github_repo_url) {
    setGithubUrl(ctx.enrollment?.githubRepoUrl || ctx.enrollment?.github_repo_url);
    setSubmissionType("github");
  }
 }

 const [t, resMap] = await Promise.all([
 loadTasks(ctx.enrollment.courseName, ctx.enrollment.experienceLevel),
 loadResources(ctx.enrollment.courseName, ctx.enrollment.experienceLevel),
 ]);

 const store = readProgress(userId, ctx.enrollment.courseName);
 if (alive) {
 setTasks(t);
 setResourcesByTaskId(resMap);
 setCompletedTaskIds(store.completedTaskIds);
 setReviewStateById(store.reviewStateById || {});
 setMentorMessages(readChat(userId, ctx.enrollment.courseName));
 
 // Store total tasks count for certificate center
 const totalTasksKey = `totalTasks_${ctx.enrollment.courseName}_${ctx.enrollment.experienceLevel}`;
 localStorage.setItem(totalTasksKey, t.length.toString());
 console.log(`📊 Stored total tasks count: ${t.length} for course: ${ctx.enrollment.courseName}`);
 
 setLoading(false);
 setNotAllowed("");
 }
 } catch (err) {
 if (alive) { setNotAllowed(String(err?.message ||"Access restricted")); setLoading(false); }
 }
 })();
 return () => { alive = false; };
 }, [userId]);

 const [analytics, setAnalytics] = useState(null);

 useEffect(() => {
   if (!userId) return;
   const sessionToken = localStorage.getItem('session_token');
   const headers = { 
     'Content-Type': 'application/json',
     'user-id': userId
   };
   if (sessionToken) {
     headers['Authorization'] = `Bearer ${sessionToken}`;
     headers['X-Session-Token'] = sessionToken;
   }
   fetch(`${API_BASE_URL}/api/user/analytics?userId=${encodeURIComponent(userId)}`, {
     headers
   })
     .then(res => res.json())
     .then(data => {
       if (data.success) setAnalytics(data);
     })
     .catch(err => console.error('Failed to fetch analytics:', err));
 }, [userId]);

 // ✅ Check LLM Trial Status on component mount and auto-show welcome modal
 useEffect(() => {
 if (userId) {
 (async () => {
 const status = await checkLLMTrialStatus(userId);
 setLlmTrialStatus(status);
 console.log('🎯 LLM Trial Status (Certified):', status);
 
 // Auto-show welcome modal for new trial users (first time only)
 if (status?.hasActiveTrial && status?.source === 'trial') {
 const welcomeShownKey = `llm_trial_welcome_shown_${userId}`;
 const welcomeShown = localStorage.getItem(welcomeShownKey);
 if (!welcomeShown) {
 setShowTrialModal(true);
 localStorage.setItem(welcomeShownKey, 'true');
 }
 }
 })();
 }
 }, [userId]);

 const go = (p) => navigate(p);

 // Voice command handlers
 const onVoiceCommand = (command) => {
 const result = handleVoiceCommand(command);
 
 switch (result.type) {
 case 'open-mentor':
 setShowMentor(true);
 break;
 case 'open-video':
 openCurrentTask('video');
 break;
 case 'open-resources':
 openCurrentTask('resources');
 break;
 case 'open-submit':
 openCurrentTask('submit');
 break;
 case 'open-feedback':
 openCurrentTask('feedback');
 break;
 case 'open-visualize':
 openCurrentTask('visualize');
 break;
 case 'start-task':
 if (tasks.length > 0) {
 openCurrentTask('overview');
 }
 break;
 case 'next-task':
 if (viewIndex !== null && viewIndex < tasks.length - 1) {
 openTask(viewIndex + 1);
 }
 break;
 case 'previous-task':
 if (viewIndex !== null && viewIndex > 0) {
 openTask(viewIndex - 1);
 }
 break;
 case 'get-help':
 setShowMentor(true);
 break;
 case 'explain-current':
 if (viewingTask) {
 generateTopicExplanation();
 }
 break;
 }
 };

 const onVoiceNavigate = (path) => {
 const result = handleVoiceNavigation(path);
 if (result.type === 'navigate') {
 navigate(result.path);
 }
 };

 const cacheBustUrl = (baseUrl) => `${baseUrl}${baseUrl.includes("?") ?"&" :"?"}v=${Date.now()}`;
 const stopVideoPolling = () => {
 if (videoPollRef.current) {
 clearInterval(videoPollRef.current);
 videoPollRef.current = null;
 }
 videoPollInFlightRef.current = false;
 videoPollStartedAtRef.current = null;
 };
 const startVideoPolling = (baseUrl, originProbeUrl) => {
 stopVideoPolling();
 videoBaseUrlRef.current = baseUrl;
 videoPollStartedAtRef.current = Date.now();

 const checkAvailability = async () => {
 const currentStatus = videoStatusRef.current;
 if (!videoBaseUrlRef.current || currentStatus ==="ready" || currentStatus ==="error") {
 return;
 }
 if (videoPollInFlightRef.current) return;
 videoPollInFlightRef.current = true;

 // Use backend endpoint to check if video exists (no CORS issues)
 const baseUrl = videoBaseUrlRef.current;
 if (!baseUrl) {
 videoPollInFlightRef.current = false;
 return;
 }
 
 try {
 // Check via backend endpoint - this avoids CORS issues
 const checkUrl = `${API_BASE_URL}/api/video/check-exists?url=${encodeURIComponent(baseUrl)}`;
 const res = await fetch(checkUrl, {
 method:"GET",
 cache:"no-store",
 });
 
 if (!res.ok) {
 videoPollInFlightRef.current = false;
 return;
 }
 
 const data = await res.json();
 
 if (data.ok && data.exists && data.status === 'ready') {
 // Video is ready!
 stopVideoPolling();
 const proxiedBaseUrl = getProxiedVideoUrl(baseUrl);
 const finalUrl = cacheBustUrl(proxiedBaseUrl);
 videoStatusRef.current ="ready";
 setVideoUrl(finalUrl);
 setVideoStatus("ready");
 setVideoMessage("Your tutorial video is ready to watch!");
 setVideoError("");
 videoErrorRetriesRef.current = 0;
 console.log("[Video Polling] Video confirmed ready via backend:", finalUrl);
 return;
 } else if (data.ok === false || data.error || data.status === 'failed') {
 // Video generation failed or error occurred
 stopVideoPolling();
 videoStatusRef.current ="error";
 setVideoStatus("error");
 const errorMessage = data.error || data.message ||"Video generation failed. Please try regenerating.";
 setVideoError(errorMessage);
 setVideoMessage("");
 console.error("[Video Polling] Video generation failed:", {
 error: data.error,
 message: data.message,
 status: data.status
 });
 return;
 } else {
 // Video not ready yet, continue polling
 console.debug("[Video Polling] Video not ready yet, status:", data.status,"exists:", data.exists);
 }
 } catch (err) {
 // Log errors but don't stop polling immediately - video might still be processing
 console.warn("[Video Polling] Check failed:", err?.message || err);
 // If we've been polling for more than 3 minutes and getting errors, it might be a real issue
 const startedAt = videoPollStartedAtRef.current;
 if (startedAt && Date.now() - startedAt > 3 * 60 * 1000) {
 console.error("[Video Polling] Persistent errors after 3 minutes - video generation may have failed");
 }
 } finally {
 videoPollInFlightRef.current = false;
 }

 const startedAt = videoPollStartedAtRef.current;
 // Reduce timeout to 5 minutes since video generation should complete faster
 if (startedAt && Date.now() - startedAt > 5 * 60 * 1000) {
 stopVideoPolling();
 videoStatusRef.current ="error";
 setVideoStatus("error");
 setVideoError("Video generation timed out after 5 minutes. The video may have failed to generate. Please try regenerating.");
 setVideoMessage("");
 console.warn("[Video Polling] Timeout after 5 minutes");
 }
 };

 checkAvailability();
 videoPollRef.current = setInterval(checkAvailability, 12000);
 };

 useEffect(() => {
 return () => {
 stopVideoPolling();
 };
 }, []);

 const handleVideoLoaded = () => {
 console.log("[Video Player] Video loaded successfully");
 stopVideoPolling();
 videoStatusRef.current ="ready";
 setVideoStatus("ready");
 setVideoMessage("Your tutorial video is ready to watch!");
 setVideoError("");
 videoErrorRetriesRef.current = 0;
 };

 const handleVideoLoadError = (e) => {
 const currentStatus = videoStatusRef.current || videoStatus;
 const currentUrl = videoUrl;
 const base = videoBaseUrlRef.current;
 
 // Don't fallback to direct CloudFront URL as it will cause CORS errors
 // The proxy URL should work - if it doesn't, the video might still be processing
 // Continue with normal error handling
 
 if (currentStatus ==="queued" || currentStatus ==="generating") {
 // Video is still rendering, this is expected - don't show error yet
 // Silently ignore errors during queued/generating status
 return;
 }
 
 // Only log errors if video is not in queued/generating status
 console.warn("[Video Player] Video load error:", {
 status: currentStatus,
 url: currentUrl,
 baseUrl: base,
 error: e?.target?.error,
 networkState: e?.target?.networkState,
 readyState: e?.target?.readyState,
 retries: videoErrorRetriesRef.current
 });
 
 // If status is"ready" but video fails, it might be a false positive from polling
 // Reset to"queued" and let polling continue
 if (currentStatus ==="ready" && videoErrorRetriesRef.current < 3) {
 console.log("[Video Player] Video marked ready but failed to load, resetting to queued");
 videoStatusRef.current ="queued";
 setVideoStatus("queued");
 setVideoMessage("Video is still processing, please wait...");
 // Don't increment retry count for this case
 return;
 }
 
 if (base && videoErrorRetriesRef.current < 5) {
 videoErrorRetriesRef.current += 1;
 console.log(`[Video Player] Retrying video load (attempt ${videoErrorRetriesRef.current}/5)`);
 setTimeout(() => {
 // Always use proxy URL to avoid CORS errors
 const retryUrl = cacheBustUrl(getProxiedVideoUrl(base));
 console.log(`[Video Player] Retrying with proxy URL: ${retryUrl}`);
 setVideoUrl(retryUrl);
 }, 3000 * videoErrorRetriesRef.current);
 } else {
 console.error("[Video Player] Video failed to load after retries", {
 finalUrl: currentUrl,
 baseUrl: base,
 status: currentStatus
 });
 setVideoStatus("error");
 setVideoError(`Video failed to load. The video may not be available yet or the URL may be incorrect. Please try regenerating the video.`);
 // Reset retry counter for next attempt
 videoErrorRetriesRef.current = 0;
 }
 };

 const sendMentorMessage = async ({ text, imgFile }) => {
  if (!text && !imgFile) return;
  const you = { id: `u-${Date.now()}`, role: "user", text, imagePreview: imgFile ? URL.createObjectURL(imgFile) : null, when: new Date().toISOString() };
  const thinking = { id: `t-${Date.now()}`, role: "assistant", text: "Thinking…", typing: true, when: new Date().toISOString() };
  const add = (arr) => { setMentorMessages(arr); writeChat(userId, courseName, arr); };

  const now = [...mentorMessages, you, thinking];
  add(now);
  const task = tasks[unlockedIndex];

  // Enhance text with technology context if it's a general question
  let enhancedText = text;
  if (text && text.length < 100 && !text.toLowerCase().includes(technology.toLowerCase())) {
    enhancedText = `Regarding ${technology}: ${text}`;
  }

  const res = await askMentorAPI({ userId, courseName, experienceLevel, task, text: enhancedText, imgFile, history: mentorMessages });
  const final = [...now.filter(m => !m.typing), { id: `a-${Date.now()}`, role: "assistant", text: res.answer || "I'm here to help.", when: new Date().toISOString() }];
  add(final);
};

 const handleVisualize = async () => {
 if (!viewingTask) return;
 setVisualizing(true);
 setVisualUrl("");
 setVisualGeneratedAt(null);
 try {
 const data = await visualizeOutputAPI({
 userId, courseName, experienceLevel, task: viewingTask, hint: visualHint
 });
 const imageUrl = data.imageUrl || data.visualUrl || data.imageData ||"";
 if (imageUrl) {
 setVisualUrl(imageUrl);
 setVisualGeneratedAt(new Date());
 setModalTab("visualize");
 console.log('✅ Visualization generated successfully');
 } else {
 throw new Error("Visualization service did not return an image URL");
 }
 } catch (e) {
 alert(e.message ||"Failed to generate image");
 } finally { setVisualizing(false); }
 };

 const generateTopicExplanation = async () => {
 if (!viewingTask) return;
 setExplanationLoading(true);
 setTopicExplanation(null);
 try {
 const data = await generateTopicExplanationAPI({
 userId, courseName, experienceLevel,
 task: viewingTask,
 language: explanationLanguage
 });
 // Backend returns: { ok: true, explanation: { explanation: explanationData, ..., usage: {...} } }
 // Extract the nested explanation data
 const explanationData = data.explanation?.explanation || data.explanation;
 setTopicExplanation(explanationData);
 if (data.explanation?.usage) {
   setExplanationUsage(data.explanation.usage);
 }
 setModalTab("resources");
 } catch (e) {
 console.error("Topic explanation error:", e);
 // Provide more helpful error message
 const errorMessage = e.message.includes("HTML instead of JSON") 
 ?"Backend server is not running or endpoint not found. Please ensure the backend server is running on port 5000."
 : e.message ||"Failed to generate topic explanation. Please try again.";
 alert(errorMessage);
 } finally { setExplanationLoading(false); }
 };

 const generateVideo = async () => {
 if (!viewingTask) return;
 
 const confirmed = window.confirm(
 `Generate tutorial video for"${viewingTask.topic}"?\n\n` +
 `This will create a comprehensive video tutorial covering:\n` +
 `• ${viewingTask.subtopics?.length || 0} subtopics\n` +
 `• Practical examples and code\n` +
 `• Real-world applications\n` +
 `• Best practices\n\n` +
 `Estimated time: 1-2 minutes\n` +
 `Video length: 60-90 seconds`
 );
 
 if (!confirmed) return;
 
 setVideoGenerating(true);
 videoStatusRef.current ="generating";
 setVideoStatus("generating");
 setVideoMessage("");
 setVideoError("");
 stopVideoPolling();
 setVideoUrl(null);
 
 try {
 const data = await generateVideoAPI({
 userId,
 task: viewingTask,
 courseType:"certified",
 experienceLevel,
 language: selectedLanguage
 });
 
 const videoPath = data.videoUrl || data.expectedUrl;
 if (!videoPath) {
 throw new Error("Video generation service did not return a video URL.");
 }
 const absoluteUrl = videoPath.startsWith("http")
 ? videoPath
 : `${API_BASE_URL}${videoPath.startsWith("/") ? videoPath : `/${videoPath}`}`;

 // Use proxy URL for CloudFront videos to avoid CORS issues
 const proxiedAbsoluteUrl = getProxiedVideoUrl(absoluteUrl);
 const originProbeUrl = (data.originUrl && data.originUrl.startsWith("http"))
 ? getProxiedVideoUrl(data.originUrl)
 : null;

 if (data.queued) {
 videoStatusRef.current ="queued";
 setVideoStatus("queued");
 setVideoMessage("Rendering video… This usually takes 1-3 minutes. We'll refresh automatically.");
 // Don't set video URL yet - wait until backend confirms it's ready
 // This prevents the video element from trying to load a non-existent video
 setVideoUrl(null);
 // Start polling backend endpoint to detect when video is ready (no CORS issues)
 startVideoPolling(absoluteUrl, originProbeUrl);
 setModalTab("video");
 console.log("[Video Generation] Video queued, polling backend to detect when ready:", absoluteUrl);
 alert(
 `🎬 Video rendering started for"${viewingTask.topic}".\n\n` +
 `Hang tight for ~2 minutes – the player will update automatically once the final MP4 is ready.`
 );
 return;
 }

 stopVideoPolling();
 const readyUrl = cacheBustUrl(proxiedAbsoluteUrl);
 videoStatusRef.current ="ready";
 setVideoUrl(readyUrl);
 setVideoStatus("ready");
 if (data.usage) {
   setVideoUsage(data.usage);
 }
 setVideoMessage("Your tutorial video is ready to watch!");
 setVideoError("");
 setModalTab("video");
 console.log("[Video Generation] Video ready immediately:", readyUrl);
 
 } catch (err) {
 console.error("Video generation failed:", err);
 
 let errorMessage ="Failed to generate video. Please try again.";
 
 if (err.message?.includes("HTML instead of JSON")) {
 errorMessage ="❌ Backend server is not running or Python dependencies not installed.\n\nPlease ensure:\n• Backend server is running on port 5000\n• Python dependencies are installed\n• All required packages are available";
 } else if (err.message?.includes("timeout")) {
 errorMessage ="⏱️ Video generation is taking longer than expected.\n\nThis can happen with complex topics. Please try again.";
 } else if (err.message?.includes("Missing required")) {
 errorMessage ="❌ Missing required information.\n\nPlease ensure the task has a valid topic and all required fields are filled.";
 } else if (err.message?.includes("Video generation service")) {
 errorMessage ="🔧 Video generation service is temporarily unavailable.\n\nPlease try again in a few minutes.";
 } else if (err.message) {
 errorMessage = `❌ ${err.message}`;
 }
 
 alert(errorMessage);
 videoStatusRef.current ="error";
 setVideoStatus("error");
 setVideoError(errorMessage);
 } finally {
 setVideoGenerating(false);
 }
 };

 if (loading || userId === null) {
 return (<div className="min-h-screen bg-white text-gray-900 flex items-center justify-center transition-colors duration-300"><div className="text-primary">Loading Certified Courses…</div></div>);
 }
 if (notAllowed) {
 return (
 <div className="min-h-screen bg-white text-gray-900 flex items-center justify-center p-6 transition-colors duration-300">
 <div className="max-w-md w-full rounded-xl border border-secondary/40 bg-gray-50 p-5">
 <div className="text-primary font-semibold mb-2">Access restricted</div>
 <div className="text-sm text-gray-700 whitespace-pre-line">{notAllowed}</div>
 </div>
 </div>
 );
 }

 return (
  <div className="certified-dashboard-wrapper">
    <div className="min-h-screen bg-bgMain text-body transition-colors duration-300">
 {/* header */}
 <header className="sticky top-0 z-10 border-b border-secondary/30 bg-bgHeaderFooter/90 backdrop-blur transition-colors duration-300">
 <div className="mx-auto max-w-7xl px-4 py-4 flex items-center justify-between">
 <div>
 <h1 className="text-xl md:text-2xl font-extrabold text-primary tracking-tight flex items-center gap-2">
 <ClipboardDocumentListIcon className="h-6 w-6 text-primary" /> {courseName ? `${courseName} • Certified` :"Certified"}
 </h1>
 <p className="text-sm text-muted flex items-center gap-2">
 <span>Hi {userName}</span> <span>👋</span> <span>Welcome back.</span>
 </p>
 <p className="mt-1 text-xs text-muted">
 <span className="mr-3">User ID: <span className="text-primary">{userId ||"-"}</span></span>
 <span className="mr-3">Track: <span className="text-primary capitalize">{track ||"-"}</span></span>
 <span>Experience: <span className="text-primary">{experienceLevel ||"-"}</span></span>
 </p>
 <div className="mt-2">
 <SubscriptionStatus />
 </div>
 </div>
 <div className="hidden sm:flex items-center gap-3">
 <QuickLink label="Voice Assistant" icon={<SpeakerWaveIcon className="h-4 w-4" />} onClick={openVoiceAssistant} />
 <QuickLink label="Certificate" icon={<TrophyIcon className="h-4 w-4" />} onClick={() => navigate(`/bootcamp/certificate`)} />
 <QuickLink label="AI Mentor" 
 label={llmTrialStatus?.hasActiveTrial ? `AI Mentor (${llmTrialStatus?.daysRemaining}d left)` :"AI Mentor"} 
 icon={<ChatBubbleLeftRightIcon className="h-4 w-4" />} 
 onClick={() => {
 if (llmTrialStatus?.hasActiveTrial) {
 setShowMentor(true);
 } else if (llmTrialStatus && llmTrialStatus.daysRemaining <= 0) {
 setShowTrialExpiredModal(true);
 } else {
 setShowTrialModal(true);
 }
 }}
 />
 </div>
 </div>
 </header>

 {/* main */}
 <div className="mx-auto max-w-7xl px-4 py-6 space-y-6">
 {/* Voice Assistant & Quick Actions */}
 <section className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 mb-6">
   <ActionCard
     title="Voice Assistant"
     subtitle="Interactive AI-powered voice guidance for your tasks"
     icon={<SpeakerWaveIcon className="h-6 w-6 text-primary" />}
     onClick={openVoiceAssistant}
   />
   <ActionCard
     title="Certificate"
     subtitle="View requirements or download your earned certificate"
     icon={<TrophyIcon className="h-6 w-6 text-primary" />}
     onClick={() => navigate('/bootcamp/certificate')}
   />
   <ActionCard
     title="AI Mentor"
     subtitle="Get personalized help and guidance from our AI expert"
     icon={<ChatBubbleLeftRightIcon className="h-6 w-6 text-primary" />}
     onClick={() => {
       if (llmTrialStatus?.hasActiveTrial) {
         setShowMentor(true);
       } else if (llmTrialStatus && llmTrialStatus.daysRemaining <= 0) {
         setShowTrialExpiredModal(true);
       } else {
         setShowTrialModal(true);
       }
     }}
   />
 </section>

 {/* Elite Progress Widget */}
 <section className="grid grid-cols-1 md:grid-cols-2 gap-6">
   {analytics?.latest_activity?.[0] && (
     <div className="rounded-2xl border border-secondary/40 bg-white p-5 shadow-sm">
       <div className="flex items-center justify-between mb-4">
         <h3 className="text-sm font-bold uppercase tracking-wider text-gray-500">Currently Learning</h3>
         <SparklesIcon className="h-5 w-5 text-purple-500" />
       </div>
       <div className="space-y-4">
         <div>
           <p className="text-xs text-muted">Technology:</p>
           <p className="text-sm font-bold text-gray-900 capitalize">{analytics.latest_activity[0].technology.replace('-premium', '')}</p>
         </div>
         <div className="grid grid-cols-2 gap-4">
           <div>
             <p className="text-xs text-muted">Topic:</p>
             <p className="text-sm font-semibold text-gray-700 truncate">{analytics.latest_activity[0].topic}</p>
           </div>
           <div>
             <p className="text-xs text-muted">Subtopic:</p>
             <p className="text-xs text-gray-600 truncate">{analytics.latest_activity[0].subtopic}</p>
           </div>
         </div>
         <div className="pt-2">
           <div className="flex justify-between text-[10px] font-bold mb-1">
             <span className="text-muted">Progress</span>
             <span className="text-primary">{Math.round(analytics.latest_activity[0].completion_percentage)}%</span>
           </div>
           <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden">
             <div 
               className="h-full bg-primary" 
               style={{ width: `${analytics.latest_activity[0].completion_percentage}%` }}
             />
           </div>
         </div>
         <button 
           onClick={() => navigate('/elite-mastery')}
           className="w-full mt-2 py-2.5 rounded-xl bg-primary text-white text-xs font-bold hover:bg-primary/90 transition-all flex items-center justify-center gap-2"
         >
           Continue Learning <ArrowRightIcon className="h-4 w-4" />
         </button>
       </div>
     </div>
   )}

   {/* MCQ Activity Widget */}
   {analytics?.mcq_activity && analytics.mcq_activity.length > 0 && (
     <div className="rounded-2xl border border-secondary/40 bg-white p-5 shadow-sm">
       <div className="flex items-center justify-between mb-4">
         <h3 className="text-sm font-bold uppercase tracking-wider text-gray-500">Recent MCQ Activity</h3>
         <ClipboardDocumentListIcon className="h-5 w-5 text-blue-500" />
       </div>
       <div className="space-y-3 max-h-[220px] overflow-y-auto pr-1">
         {analytics.mcq_activity.map((activity, idx) => (
           <div key={idx} className="flex items-center justify-between p-2 rounded-lg bg-gray-50 border border-gray-100">
             <div className="min-w-0">
               <div className="flex items-center gap-2">
                 <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-bold uppercase ${
                   activity.source === 'TechGrid' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'
                 }`}>
                   {activity.source}
                 </span>
                 <span className="text-[9px] text-gray-500">{new Date(activity.date).toLocaleDateString()}</span>
               </div>
               <p className="text-sm font-bold text-gray-900 truncate mt-1 capitalize">{activity.name.replace(/-/g, ' ')}</p>
               <p className="text-[10px] text-gray-600">Level {activity.level}</p>
             </div>
             <div className="text-right ml-2">
               <p className={`text-sm font-bold ${Number(activity.score) >= 70 ? 'text-green-600' : 'text-red-600'}`}>
                 {Math.round(activity.score)}%
               </p>
               <p className="text-[9px] font-medium text-gray-500">
                 {Number(activity.score) >= 70 ? 'Passed' : 'Failed'}
               </p>
             </div>
           </div>
         ))}
       </div>
     </div>
   )}
 </section>

  {/* main */}
  <main className="mx-auto max-w-7xl px-4 py-6">
    <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
      {/* Sidebar: Task List */}
      <aside className="lg:col-span-1 space-y-4">
        <div className={`p-4 rounded-2xl border ${isDark ? "bg-bgSection border-accentGray/20" : "bg-white border-secondary/40 shadow-sm"}`}>
          <h3 className={`font-bold mb-4 ${isDark ? "text-white" : "text-gray-900"}`}>Course Roadmap</h3>
          <div className="space-y-2 max-h-[600px] overflow-y-auto pr-2">
            {tasks.map((t, idx) => {
              const isLocked = idx > unlockedIndex;
              const isCompleted = completedTaskIds.includes(t.id);
              const isActive = viewIndex === idx;
              
              return (
                <button
                  key={t.id}
                  disabled={isLocked}
                  onClick={() => setViewIndex(idx)}
                  className={`w-full text-left p-3 rounded-xl border transition-all flex items-center gap-3 ${
                    isActive 
                      ? "border-primary bg-primary/10" 
                      : isLocked 
                        ? "opacity-50 cursor-not-allowed border-transparent" 
                        : isDark ? "border-accentGray/10 hover:border-accentGray/30" : "border-gray-100 hover:border-primary/30"
                  }`}
                >
                  <div className={`h-8 w-8 rounded-lg flex items-center justify-center shrink-0 ${
                    isCompleted ? "bg-green-500/10 text-green-500" : isActive ? "bg-primary text-white" : "bg-secondary/10 text-muted"
                  }`}>
                    {isCompleted ? <CheckCircleIconSolid className="h-5 w-5" /> : <span className="text-sm font-bold">{idx + 1}</span>}
                  </div>
                  <div className="min-w-0">
                    <p className={`text-sm font-bold truncate ${isActive ? "text-primary" : isDark ? "text-white" : "text-gray-900"}`}>{t.title || t.topic}</p>
                    <p className="text-xs text-muted truncate">{isLocked ? "Locked" : isCompleted ? "Completed" : "In Progress"}</p>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </aside>

      {/* Main Content: 4 Sections */}
      <div className="lg:col-span-3 space-y-6">
        <div className={`p-6 rounded-2xl border ${isDark ? "bg-bgSection border-accentGray/20" : "bg-white border-secondary/40 shadow-sm"}`}>
          {/* Header & Tabs */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
            <div>
              <p className="text-xs uppercase tracking-wider text-muted font-bold">Task {viewIndex + 1} of {totalTasks}</p>
              <h2 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>{tasks[viewIndex]?.title || tasks[viewIndex]?.topic}</h2>
            </div>
            <div className="flex flex-wrap gap-2">
              <TabButton active={modalTab === "overview"} onClick={() => setModalTab("overview")}>Overview</TabButton>
              <TabButton active={modalTab === "resources"} onClick={() => setModalTab("resources")}>AI Resources</TabButton>
              <TabButton active={modalTab === "video"} onClick={() => setModalTab("video")}>Video Tutorial</TabButton>
              <TabButton active={modalTab === "submit"} onClick={() => setModalTab("submit")}>Submit AI Review</TabButton>
              <TabButton active={modalTab === "feedback"} onClick={() => setModalTab("feedback")}>Feedback</TabButton>
            </div>
          </div>

          {/* Tab Content */}
          <div className="min-h-[400px]">
            {tasks[viewIndex] ? (
              <>
                {modalTab === "overview" && (
                  <div className="space-y-6 animate-in fade-in slide-in-from-bottom-2 duration-300">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-4">
                        <Field label="Description">
                          <p className={`text-sm leading-relaxed ${isDark ? "text-gray-300" : "text-gray-600"}`}>{tasks[viewIndex].description}</p>
                        </Field>
                        {tasks[viewIndex].useCase && (
                          <Info label="Use Case">
                            <DocumentTextIcon className="h-4 w-4 text-primary" /> {tasks[viewIndex].useCase}
                          </Info>
                        )}
                      </div>
                      <div className="space-y-4">
                        {tasks[viewIndex].expectedOutput && (
                          <Info label="Expected Output">
                            <CheckCircleIcon className="h-4 w-4 text-primary" /> {tasks[viewIndex].expectedOutput}
                          </Info>
                        )}
                        {tasks[viewIndex].subtopics?.length > 0 && (
                          <div className={`p-4 rounded-xl ${isDark ? "bg-accentGray/5" : "bg-gray-50"}`}>
                            <p className="text-sm font-bold mb-2">Key Topics:</p>
                            <div className="flex flex-wrap gap-2 mt-2">
                              {tasks[viewIndex].subtopics.map((s, i) => (
                                <button
                                  key={i}
                                  onClick={() => explainWithAI(s)}
                                  className="px-3 py-1.5 bg-primary/5 hover:bg-primary/20 border border-primary/20 rounded-lg text-sm text-primary font-medium flex items-center gap-2 transition-all group text-left"
                                >
                                  {s}
                                  <ChatBubbleLeftRightIcon className="h-4 w-4 opacity-50 group-hover:opacity-100" />
                                </button>
                              ))}
                            </div>
                            <p className="text-[10px] text-muted mt-3">Click on any subtopic to get a detailed explanation from our AI Assistant.</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {modalTab === "resources" && (
                  <div className="animate-in fade-in slide-in-from-bottom-2 duration-300 space-y-6">
                    <div className="text-center py-12">
                      <DocumentTextIcon className="h-12 w-12 text-primary/20 mx-auto mb-4" />
                      <p className="text-muted">Loading AI Resources...</p>
                      <Button variant="contained" className="mt-4" onClick={() => openCurrentTask("resources")}>Generate Resources</Button>
                    </div>
                  </div>
                )}

                {modalTab === "video" && (
                  <div className="animate-in fade-in slide-in-from-bottom-2 duration-300 text-center py-12">
                    <PlayCircleIcon className="h-12 w-12 text-primary/20 mx-auto mb-4" />
                    <p className="text-muted">Video Tutorial will appear here.</p>
                  </div>
                )}

                {modalTab === "submit" && (
                  <div className="animate-in fade-in slide-in-from-bottom-2 duration-300 space-y-6">
                    <div className={`p-6 rounded-2xl border-2 border-dashed ${isDark ? "border-accentGray/20" : "border-gray-200"}`}>
                      <h4 className="font-bold mb-4 flex items-center gap-2">
                        <GitHubIcon className="h-5 w-5" /> GitHub Repository
                      </h4>
                      {(githubRepoUrl || githubStatus?.repo_url) ? (
                        <div className="space-y-4">
                          <div className={`p-4 rounded-xl ${isDark ? "bg-accentGray/10" : "bg-gray-50"} flex items-center justify-between gap-3`}>
                            <code className="text-sm text-primary truncate flex-1">{githubRepoUrl || githubStatus?.repo_url}</code>
                            <div className="flex gap-2">
                              <button 
                                onClick={syncInvitationStatus}
                                className="p-2 rounded-lg hover:bg-primary/10 transition-colors text-primary"
                                title="Refresh status"
                              >
                                <ArrowPathIcon className="h-4 w-4" />
                              </button>
                              <a 
                                href={githubRepoUrl || githubStatus?.repo_url} 
                                target="_blank" 
                                rel="noreferrer" 
                                className="px-3 py-1 bg-primary text-white text-xs font-bold rounded-lg hover:bg-secondary transition-colors flex items-center gap-1"
                              >
                                <ArrowTopRightOnSquareIcon className="h-3 w-3" /> Open
                              </a>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 text-sm">
                            <div className={`h-2 w-2 rounded-full ${(invitationStatus === 'active' || invitationStatus === 'ACCEPTED' || invitationStatus === 'READY') ? 'bg-green-500' : 'bg-yellow-500'}`} />
                            <span className="text-muted">Invitation Status: <span className="font-bold capitalize">{invitationStatus || 'pending'}</span></span>
                          </div>
                          {(invitationStatus !== 'active' && invitationStatus !== 'ACCEPTED' && invitationStatus !== 'READY') && (
                            <div className="space-y-3">
                              <p className="text-xs text-muted">Please check your email and accept the GitHub invitation to enable AI reviews.</p>
                              <Button 
                                size="small" 
                                variant="outlined" 
                                onClick={syncInvitationStatus}
                                startIcon={<ArrowPathIcon className="h-4 w-4" />}
                              >
                                Check Invitation Status
                              </Button>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="space-y-3">
                          <div className="flex items-center justify-between">
                            <p className="text-sm text-muted italic">No repository provisioned. Please contact support if you enrolled with a GitHub username.</p>
                            <button 
                              onClick={syncInvitationStatus}
                              className="text-xs text-primary font-bold hover:underline flex items-center gap-1"
                            >
                              <ArrowPathIcon className="h-3 w-3" /> Check Status
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                    
                    <div className="flex justify-end">
                      <Button 
                        variant="contained" 
                        disabled={!(invitationStatus === 'active' || invitationStatus === 'ACCEPTED' || invitationStatus === 'READY')}
                        onClick={() => openCurrentTask("submit")}
                      >
                        Proceed to Review
                      </Button>
                    </div>
                  </div>
                )}
                
                {modalTab === "feedback" && (
                  <div className="animate-in fade-in slide-in-from-bottom-2 duration-300 text-center py-12">
                    <ChatBubbleLeftRightIcon className="h-12 w-12 text-primary/20 mx-auto mb-4" />
                    <p className="text-muted">No feedback available yet. Submit your task first.</p>
                  </div>
                )}
              </>
            ) : (
              <div className="flex flex-col items-center justify-center py-20">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
                <p className="mt-4 text-muted font-medium">Preparing your workspace...</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>

 </main>

 {/* Task modal */}
 {showModal && viewingTask && (
 <TaskModal
 key={`${viewIndex}-${viewingTask.id}`}
 task={viewingTask}
 resources={resourcesByTaskId[viewingTask.id] || []}
 onClose={() => setShowModal(false)}
 tab={modalTab}
 setTab={setModalTab}
        reviewState={reviewStateById[viewingTask.id]}
        courseName={courseName}
        language={selectedLanguage}
        setLanguage={() => {}} // Language selection removed - always English
        githubRepoUrl={githubRepoUrl}
        invitationStatus={invitationStatus}
        githubUrl={githubUrl}
        setGithubUrl={setGithubUrl}
        submissionType={submissionType}
        setSubmissionType={setSubmissionType}
        explanationUsage={explanationUsage}
        videoUsage={videoUsage}
        onSubmit={async ({ docFile, imgFile, comments, githubUrl, submissionType, setSubmitting, setServerError }) => {
          try {
            setServerError(""); setSubmitting(true);
            const pendingMap = { ...reviewStateById, [viewingTask.id]: { status: "pending" } };
            writeProgress(userId, courseName, { completedTaskIds, reviewStateById: pendingMap });
            setReviewStateById(pendingMap);

            // Use provisioned GitHub URL if available
            const finalGithubUrl = githubRepoUrl || githubUrl;
            const finalSubmissionType = githubRepoUrl ? "github" : submissionType;

            const result = await reviewTaskWithAI({
              userId, courseName, experienceLevel,
              task: viewingTask, docFile, imgFile, comments, 
              githubUrl: finalGithubUrl, 
              submissionType: finalSubmissionType,
            });

 const nextReview = {
 status: result.passed ?"passed" :"failed",
 feedback: result.feedback ||"",
 score: result.score ?? null,
 details: result.details || null
 };
 const nextReviewState = { ...pendingMap, [viewingTask.id]: nextReview };
 let nextCompleted = completedTaskIds;
 if (result.passed) {
 nextCompleted = Array.from(new Set([...completedTaskIds, viewingTask.id]));
 // Mark task as completed in calendar when task is passed
 markTaskCompleted();
 }
 setCompletedTaskIds(nextCompleted);
 setReviewStateById(nextReviewState);
 writeProgress(userId, courseName, { completedTaskIds: nextCompleted, reviewStateById: nextReviewState });
 setModalTab("feedback");
 } catch (e) {
 setServerError(String(e?.message || e));
 } finally { setSubmitting(false); }
 }}
 visualize={{
 show: true,
 visualizing,
 visualUrl,
 visualHint,
 visualGeneratedAt,
 setVisualHint,
 onVisualize: handleVisualize
 }}
 explanation={{
 loading: explanationLoading, explanation: topicExplanation, onGenerate: generateTopicExplanation, language: explanationLanguage
 }}
 explanationLanguage={explanationLanguage}
 setExplanationLanguage={() => {}} // Language selection removed - always English
 video={{
 generating: videoGenerating,
 url: videoUrl,
 status: videoStatus,
 message: videoMessage,
 error: videoError,
 onGenerate: generateVideo,
 onLoaded: handleVideoLoaded,
 onError: handleVideoLoadError,
 language: selectedLanguage
 }}
 />
 )}

 {/* 🎯 LLM Trial Active Modal */}
 {showTrialModal && llmTrialStatus?.hasActiveTrial && (
 <div className="fixed inset-0 z-50 flex items-center justify-center bg-bgMain/70 p-4">
 <div className="w-full max-w-md bg-gradient-to-br from-blue-900 via-blue-800 to-indigo-900 border border-blue-400/50 rounded-2xl shadow-2xl p-8 text-center">
 <div className="text-5xl mb-4">🎉</div>
 <h2 className="text-2xl font-bold text-blue-100 mb-4">Welcome to AI Mentor!</h2>
 <p className="text-blue-200 mb-6 text-sm leading-relaxed">
 You have a <strong className="text-primary">7-day FREE trial</strong> to access our premium AI Mentor feature. Get personalized guidance, ask questions, and accelerate your learning!
 </p>
 <div className="bg-blue-800/50 border border-blue-400/30 rounded-lg p-4 mb-6">
 <p className="text-blue-100 font-semibold">
 Trial expires in <span className="text-primary text-lg">{llmTrialStatus?.daysRemaining}</span> days
 </p>
 </div>
 <button
 onClick={() => {
 setShowTrialModal(false);
 setShowMentor(true);
 }}
 className="w-full bg-primary text-white font-bold py-3 rounded-lg hover:bg-secondary transition mb-3"
 >
 Start Your Free Trial →
 </button>
 <button
 onClick={() => setShowTrialModal(false)}
 className="w-full bg-blue-700/50 border border-blue-400/50 text-blue-100 font-medium py-2 rounded-lg hover:bg-blue-700 transition"
 >
 Maybe Later
 </button>
 </div>
 </div>
 )}

 {/* 🔒 LLM Trial Expired Modal */}
 {showTrialExpiredModal && (
 <div className="fixed inset-0 z-50 flex items-center justify-center bg-bgMain/70 p-4">
 <div className="w-full max-w-md bg-gradient-to-br from-red-900 via-orange-900 to-red-900 border border-red-400/50 rounded-2xl shadow-2xl p-8 text-center">
 <div className="text-5xl mb-4">⏰</div>
 <h2 className="text-2xl font-bold text-red-100 mb-4">Trial Period Expired</h2>
 <p className="text-red-200 mb-6 text-sm leading-relaxed">
 Your <strong className="text-primary">7-day free trial</strong> for AI Mentor has ended. Upgrade to <strong className="text-primary">Premium</strong> to continue accessing advanced features!
 </p>
 <div className="bg-red-800/50 border border-red-400/30 rounded-lg p-4 mb-6">
 <ul className="text-red-100 text-sm space-y-2 text-left">
 <li>✨ Unlimited AI Mentor Access</li>
 <li>🎯 Advanced Challenge Levels</li>
 <li>📊 Personalized Learning Paths</li>
 <li>🏆 Certificate Programs</li>
 </ul>
 </div>
 <button
 onClick={() => {
 setShowTrialExpiredModal(false);
 navigate('/subscription-plans');
 }}
 className="w-full bg-primary text-white font-bold py-3 rounded-lg hover:bg-secondary transition mb-3"
 >
 Upgrade to Premium Now
 </button>
 <button
 onClick={() => setShowTrialExpiredModal(false)}
 className="w-full bg-red-700/50 border border-red-400/50 text-red-100 font-medium py-2 rounded-lg hover:bg-red-700 transition"
 >
 Close
 </button>
 </div>
 </div>
 )}

 {/* Mentor modal */}
 {showMentor && (
 <MentorModal
 onClose={() => setShowMentor(false)}
 messages={mentorMessages}
 setMessages={(m) => { setMentorMessages(m); writeChat(userId, courseName, m); }}
 onSend={sendMentorMessage}
 technology={courseName}
 task={tasks[unlockedIndex]}
 />
 )}

 {/* Voice Assistant */}
 <VoiceAssistant
   isOpen={isVoiceAssistantOpen}
   onClose={closeVoiceAssistant}
   initialMessage={vaInitialMessage}
   onCommand={onVoiceCommand}
   onNavigate={onVoiceNavigate}
   currentTask={viewingTask}
   userContext={{
     userId,
     courseName,
     experienceLevel,
     currentTaskIndex: viewIndex,
     totalTasks: tasks.length
   }}
 />
 <AchievementSystem userId={userId} userProgress={enrollment?.progress} dashboardType="certified" />
 </div>
 </div>
 </div>
 );
}

/* --------------------------------- UI bits -------------------------------- */
function QuickLink({ label, icon, onClick }) {
 return (
 <button onClick={onClick} className="flex items-center gap-1 rounded-xl border border-secondary/50 text-primary hover:text-primary hover:border-primary transition px-4 py-2 text-sm">
 {icon} {label}
 </button>
 );
}
function ActionCard({ title, subtitle, icon, onClick }) {
 return (
 <button onClick={onClick} className="flex flex-col items-start gap-3 rounded-xl border border-secondary/30 bg-bgMain/40 p-5 text-left hover:border-primary hover:bg-secondary/5 transition-all duration-200 group">
 <div className="flex items-center gap-3">
 <div className="p-2 rounded-lg bg-primary/10 group-hover:bg-primary/20 transition">
 {icon}
 </div>
 <h4 className="font-semibold text-sm group-hover:text-primary transition">{title}</h4>
 </div>
 <p className="text-xs text-muted group-hover:text-muted transition leading-relaxed">
 {subtitle.length > 50 ? `${subtitle.substring(0, 50)}...` : subtitle}
 </p>
 </button>
 );
}
function ProgressRing({ percent = 0, size = 200, stroke = 16 }) {
 const radius = (size - stroke) / 2, c = 2 * Math.PI * radius, dash = ((100 - percent) / 100) * c;
 return (
 <div className="flex items-center gap-4">
 <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
 <circle cx={size/2} cy={size/2} r={radius} stroke="#3f3f46" strokeWidth={stroke} fill="none"/>
 <circle cx={size/2} cy={size/2} r={radius} stroke="#fde047" strokeWidth={stroke} fill="none" strokeDasharray={c} strokeDashoffset={dash} strokeLinecap="round" transform={`rotate(-90 ${size/2} ${size/2})`}/>
 </svg>
 <div><div className="text-4xl font-extrabold text-primary">{percent}%</div><div className="text-sm text-muted">Overall progress</div></div>
 </div>
 );
}
function LinearProgress({ percent = 0 }) {
 return (
 <div className="mt-3">
 <div className="h-2 w-full rounded-full bg-zinc-800 overflow-hidden">
 <div className="h-2 bg-primary transition-all" style={{ width: `${clamp(percent, 0, 100)}%` }} />
 </div>
 <div className="mt-1 text-[11px] text-muted">{percent}% complete</div>
 </div>
 );
}

function TaskModal({ task, resources = [], onClose, tab, setTab, reviewState, onSubmit, visualize, courseName, explanation, video, language, setLanguage, explanationLanguage, setExplanationLanguage, githubRepoUrl, invitationStatus, githubUrl, setGithubUrl, submissionType, setSubmissionType, explanationUsage, videoUsage, explainWithAI }) {
  const [docFile, setDocFile] = useState(null);
  const [imgFile, setImgFile] = useState(null);
  const [comments, setComments] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState("");
 
 // TTS (Text-to-Speech) state
 const [ttsLanguage, setTtsLanguage] = useState('en');
 const [isSpeaking, setIsSpeaking] = useState(false);
 const [speechSynthesis, setSpeechSynthesis] = useState(null);
 const speechUtteranceRef = useRef(null);

 // Initialize speech synthesis
 useEffect(() => {
 if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
 setSpeechSynthesis(window.speechSynthesis);
 }
 }, []);

 // Language options for TTS
 const ttsLanguages = [
 { code: 'en', name: 'English', voice: 'en-US' },
 // Indian Languages
 { code: 'hi', name: 'Hindi (हिंदी)', voice: 'hi-IN' },
 { code: 'ta', name: 'Tamil (தமிழ்)', voice: 'ta-IN' },
 { code: 'te', name: 'Telugu (తెలుగు)', voice: 'te-IN' },
 { code: 'kn', name: 'Kannada (ಕನ್ನಡ)', voice: 'kn-IN' },
 { code: 'ml', name: 'Malayalam (മലയാളം)', voice: 'ml-IN' },
 { code: 'bn', name: 'Bengali (বাংলা)', voice: 'bn-IN' },
 { code: 'gu', name: 'Gujarati (ગુજરાતી)', voice: 'gu-IN' },
 { code: 'mr', name: 'Marathi (मराठी)', voice: 'mr-IN' },
 { code: 'pa', name: 'Punjabi (ਪੰਜਾਬੀ)', voice: 'pa-IN' },
 { code: 'ur', name: 'Urdu (اردو)', voice: 'ur-IN' },
 { code: 'or', name: 'Odia (ଓଡ଼ିଆ)', voice: 'or-IN' },
 { code: 'as', name: 'Assamese (অসমীয়া)', voice: 'as-IN' },
 // Other Languages
 { code: 'es', name: 'Spanish', voice: 'es-ES' },
 { code: 'fr', name: 'French', voice: 'fr-FR' },
 { code: 'de', name: 'German', voice: 'de-DE' },
 { code: 'it', name: 'Italian', voice: 'it-IT' },
 { code: 'pt', name: 'Portuguese', voice: 'pt-BR' },
 { code: 'ja', name: 'Japanese', voice: 'ja-JP' },
 { code: 'ko', name: 'Korean', voice: 'ko-KR' },
 { code: 'zh', name: 'Chinese', voice: 'zh-CN' },
 { code: 'ar', name: 'Arabic', voice: 'ar-SA' },
 { code: 'ru', name: 'Russian', voice: 'ru-RU' },
 ];

 // Stop speaking
 const stopSpeaking = () => {
 if (speechSynthesis && speechSynthesis.speaking) {
 speechSynthesis.cancel();
 setIsSpeaking(false);
 }
 };

 // Speak text in chunks for better flow and sync
 const speakText = (text, lang = ttsLanguage) => {
   if (!speechSynthesis || !text) return;

   // Stop any ongoing speech
   stopSpeaking();

   // Clean text (remove markdown, code blocks, etc.)
   const cleanText = text
     .replace(/```[\s\S]*?```/g, '') // Remove code blocks
     .replace(/`[^`]+`/g, '') // Remove inline code
     .replace(/#{1,6}\s+/g, '') // Remove markdown headers
     .replace(/\*\*([^*]+)\*\*/g, '$1') // Remove bold
     .replace(/\*([^*]+)\*/g, '$1') // Remove italic
     .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1') // Remove links
     .trim();

   if (!cleanText) return;

   // Split into sentences for better natural flow
   const sentences = cleanText.split(/[.!?]+\s+/).filter(s => s.trim().length > 0);
   if (sentences.length === 0) return;

   setIsSpeaking(true);
   let currentSentenceIndex = 0;

   const speakNextSentence = () => {
     if (currentSentenceIndex >= sentences.length) {
       setIsSpeaking(false);
       return;
     }

     const utterance = new SpeechSynthesisUtterance(sentences[currentSentenceIndex]);
     const selectedLang = ttsLanguages.find(l => l.code === lang) || ttsLanguages[0];
    
     utterance.lang = selectedLang.voice;
     utterance.rate = 0.95; // Slightly faster for natural flow
     utterance.pitch = 1;
     utterance.volume = 1;

     // Find voice
     const voices = speechSynthesis.getVoices();
     const voice = voices.find(v => v.lang.startsWith(selectedLang.code)) || 
                   voices.find(v => v.lang.includes(selectedLang.code)) ||
                   voices[0];
    
     if (voice) utterance.voice = voice;

     utterance.onend = () => {
       currentSentenceIndex++;
       // Small pause between sentences (250ms)
       setTimeout(speakNextSentence, 250);
     };

     utterance.onerror = () => {
       setIsSpeaking(false);
     };

     speechUtteranceRef.current = utterance;
     speechSynthesis.speak(utterance);
   };

   speakNextSentence();
 };

 // Speak full explanation
 const speakFullExplanation = () => {
 if (!explanation?.explanation) return;
 const fullText = [
 safeToString(explanation.explanation.topicOverview),
 Array.isArray(explanation.explanation.keyConcepts) ? explanation.explanation.keyConcepts.map(safeToString).join('. ') : safeToString(explanation.explanation.keyConcepts),
 Object.values(explanation.explanation.subtopicExplanations || {}).map(safeToString).join('. '),
 safeToString(explanation.explanation.practicalExamples),
 safeToString(explanation.explanation.commonMistakes),
 safeToString(explanation.explanation.bestPractices)
 ].filter(Boolean).join('. ');
 speakText(fullText);
 };

 // Cleanup on unmount
 useEffect(() => {
 return () => {
 if (speechSynthesis && speechSynthesis.speaking) {
 speechSynthesis.cancel();
 }
 };
 }, [speechSynthesis]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
      <div className="w-full max-w-4xl bg-[#f0f9ff] border border-sky-200 rounded-2xl shadow-xl overflow-hidden">
        <div className="flex items-center justify-between px-5 py-3 border-b border-sky-100">
          <div className="flex items-center gap-2">
            <ClipboardDocumentListIcon className="h-5 w-5 text-black" />
            <h3 className="font-bold text-black">Task</h3>
          </div>
 <button className="p-1 rounded hover:bg-zinc-800" onClick={onClose}><XMarkIcon className="h-5 w-5 text-muted" /></button>
 </div>

 <div className="px-5 pt-3">
 <div className="flex gap-2 flex-wrap">
            <TabButton active={tab==="overview"} onClick={()=>setTab("overview")}>Start Learning</TabButton>
            <TabButton active={tab==="resources"} onClick={()=>setTab("resources")}>AI Resources</TabButton>
            <TabButton active={tab==="video"} onClick={()=>setTab("video")}>Video Tutorial</TabButton>
            <TabButton active={tab==="submit"} onClick={()=>setTab("submit")}>Submit AI Review</TabButton>
            <TabButton active={tab==="feedback"} onClick={()=>setTab("feedback")}>Feedback</TabButton>
            </div>
 </div>

 <div className="px-5 py-4 max-h-[70vh] overflow-y-auto space-y-4">
 {tab ==="overview" && (
 <div className="space-y-4">
 <Field label="Topic"><div className="text-base font-semibold">{task.title || task.topic}</div></Field>
 {task.description && <Field label="Description"><p className="text-sm text-gray-200 whitespace-pre-line">{task.description}</p></Field>}
 {task.useCase && <Info label="Use case"><DocumentTextIcon className="h-4 w-4 text-primary" /> {task.useCase}</Info>}
 {task.futureUseCase && <Info label="Future use case"><ShieldCheckIcon className="h-4 w-4 text-primary" /> {task.futureUseCase}</Info>}
 {task.expectedOutput && <Info label="Expected output"><CheckCircleIcon className="h-4 w-4 text-primary" /> {task.expectedOutput}</Info>}
 {task.externalTools?.length ? <Info label="External tools"><span>{task.externalTools.join(",")}</span></Info> : null}
 {task.subtopics?.length > 0 && (
 <div>
 <div className="text-sm text-primary font-bold mb-2">Task Overview</div>
 <ul className="list-disc pl-5 space-y-1 text-sm text-gray-200">{task.subtopics.map((s,i)=><li key={i}>{s}</li>)}</ul>
 </div>
 )}
 </div>
 )}

 {tab ==="resources" && (
 <div className="space-y-6">
 {/* Generate AI Explanation Button */}
 <div className="rounded-xl border border-secondary/20 bg-bgMain/40 p-4">
 <div className="flex items-center justify-between mb-4">
 <h4 className="text-lg font-semibold text-primary flex items-center gap-2">
 <BookOpenIcon className="h-5 w-5" />
 AI-Powered Topic Explanation
 </h4>
 <button
 onClick={explanation.onGenerate}
 disabled={explanation.loading}
 className="rounded-xl bg-primary text-white px-4 py-2 text-sm font-semibold hover:bg-primary disabled:opacity-60 flex items-center gap-2"
 >
 {explanation.loading ? (
 <>
 <ArrowPathIcon className="h-4 w-4 animate-spin" />
 Generating...
 </>
 ) : (
 <>
 <BookOpenIcon className="h-4 w-4" />
 Generate Explanation {explanationUsage ? ` (${explanationUsage.explanations_remaining} left)` : ''}
 </>
 )}
 </button>
 </div>
 <p className="text-sm text-muted">
 Click the button above to generate a comprehensive AI-powered explanation of <strong className="text-primary">{task.topic}</strong> 
 tailored for your experience level and course.
 </p>
 </div>

 {/* AI Generated Content */}
 {explanation.explanation && (
 <div className="space-y-6">
 {/* TTS Controls */}
 <div className="rounded-xl border border-secondary/20 bg-bgMain/40 p-4">
 <div className="flex items-center justify-between mb-4">
 <h4 className="text-lg font-semibold text-primary flex items-center gap-2">
 <SpeakerWaveIcon className="h-5 w-5" />
 Listen to Explanation
 </h4>
 <div className="flex items-center gap-2">
 <select
 value={ttsLanguage}
 onChange={(e) => setTtsLanguage(e.target.value)}
 className="bg-bgMain/60 border border-secondary/30 rounded-lg px-3 py-1.5 text-sm text-gray-200 focus:outline-none focus:border-primary"
 disabled={isSpeaking}
 >
 {ttsLanguages.map((lang) => (
 <option key={lang.code} value={lang.code}>
 {lang.name}
 </option>
 ))}
 </select>
 {isSpeaking ? (
 <button
 onClick={stopSpeaking}
 className="bg-red-500 hover:bg-red-600 text-white px-4 py-1.5 rounded-lg text-sm font-semibold flex items-center gap-2"
 >
 <XMarkIcon className="h-4 w-4" />
 Stop
 </button>
 ) : (
 <button
 onClick={speakFullExplanation}
 className="bg-primary hover:bg-primary text-white px-4 py-1.5 rounded-lg text-sm font-semibold flex items-center gap-2"
 >
 <SpeakerWaveIcon className="h-4 w-4" />
 Listen All
 </button>
 )}
 </div>
 </div>
 <p className="text-xs text-muted">
 Select your preferred language and click"Listen All" to hear the complete explanation, or use the play buttons next to each section.
 </p>
 </div>

 {/* Topic Overview */}
 {explanation.explanation.topicOverview && (
 <div className="rounded-xl border border-secondary/20 bg-bgMain/40 p-4">
 <div className="flex items-center justify-between mb-3">
 <h4 className="text-lg font-semibold text-primary flex items-center gap-2">
 <span>📖</span>
 Topic Overview
 </h4>
 {!isSpeaking && (
 <button
 onClick={() => speakText(safeToString(explanation.explanation.topicOverview))}
 className="text-primary hover:text-primary p-1.5 rounded hover:bg-primary/10"
 title="Listen to this section"
 >
 <SpeakerWaveIcon className="h-5 w-5" />
 </button>
 )}
 </div>
 <p className="text-sm text-muted leading-relaxed whitespace-pre-line">
 {safeToString(explanation.explanation.topicOverview)}
 </p>
 </div>
 )}

 {/* Key Concepts */}
 {explanation.explanation.keyConcepts && explanation.explanation.keyConcepts.length > 0 && (
 <div className="rounded-xl border border-secondary/20 bg-bgMain/40 p-4">
 <div className="flex items-center justify-between mb-3">
 <h4 className="text-lg font-semibold text-primary flex items-center gap-2">
 <span>🎯</span>
 Key Concepts
 </h4>
 {!isSpeaking && (
 <button
 onClick={() => speakText(Array.isArray(explanation.explanation.keyConcepts) ? explanation.explanation.keyConcepts.map(safeToString).join('. ') : safeToString(explanation.explanation.keyConcepts))}
 className="text-primary hover:text-primary p-1.5 rounded hover:bg-primary/10"
 title="Listen to this section"
 >
 <SpeakerWaveIcon className="h-5 w-5" />
 </button>
 )}
 </div>
 <ul className="space-y-2">
 {explanation.explanation.keyConcepts.map((concept, index) => (
 <li key={index} className="flex items-start gap-2">
 <span className="text-primary text-sm font-medium bg-primary/10 px-2 py-1 rounded-full flex-shrink-0">
 {index + 1}
 </span>
 <span className="text-sm text-muted">{safeToString(concept)}</span>
 </li>
 ))}
 </ul>
 </div>
 )}

 {/* Subtopic Explanations */}
 {explanation.explanation.subtopicExplanations && Object.keys(explanation.explanation.subtopicExplanations).length > 0 && (
 <div className="rounded-xl border border-secondary/20 bg-bgMain/40 p-4">
 <div className="flex items-center justify-between mb-3">
 <h4 className="text-lg font-semibold text-primary flex items-center gap-2">
 <ClipboardDocumentListIcon className="h-5 w-5" />
 Detailed Subtopic Explanations
 </h4>
 {!isSpeaking && (
 <button
 onClick={() => speakText(Object.values(explanation.explanation.subtopicExplanations || {}).map(safeToString).join('. '))}
 className="text-primary hover:text-primary p-1.5 rounded hover:bg-primary/10"
 title="Listen to this section"
 >
 <SpeakerWaveIcon className="h-5 w-5" />
 </button>
 )}
 </div>
 <div className="space-y-4">
 {Object.entries(explanation.explanation.subtopicExplanations).map(([subtopic, explanationText], index) => (
 <div key={index} className="rounded-lg border border-secondary/10 bg-bgMain/20 p-4">
 <div className="flex items-center justify-between mb-2">
 <h5 className="text-sm font-semibold text-gray-100">{safeToString(subtopic)}</h5>
 {!isSpeaking && (
 <button
 onClick={() => speakText(safeToString(explanationText))}
 className="text-primary hover:text-primary p-1 rounded hover:bg-primary/10"
 title="Listen to this subtopic"
 >
 <SpeakerWaveIcon className="h-4 w-4" />
 </button>
 )}
 </div>
 <p className="text-sm text-muted leading-relaxed whitespace-pre-line">{safeToString(explanationText)}</p>
 </div>
 ))}
 </div>
 </div>
 )}

 {/* Practical Examples */}
 {explanation.explanation.practicalExamples && (
 <div className="rounded-xl border border-secondary/20 bg-bgMain/40 p-4">
 <div className="flex items-center justify-between mb-3">
 <h4 className="text-lg font-semibold text-primary flex items-center gap-2">
 <span>💼</span>
 Practical Examples & Use Cases
 </h4>
 {!isSpeaking && (
 <button
 onClick={() => speakText(safeToString(explanation.explanation.practicalExamples))}
 className="text-primary hover:text-primary p-1.5 rounded hover:bg-primary/10"
 title="Listen to this section"
 >
 <SpeakerWaveIcon className="h-5 w-5" />
 </button>
 )}
 </div>
 <p className="text-sm text-muted leading-relaxed whitespace-pre-line">
 {safeToString(explanation.explanation.practicalExamples)}
 </p>
 </div>
 )}

 {/* Common Mistakes */}
 {explanation.explanation.commonMistakes && (
 <div className="rounded-xl border border-red-500/20 bg-red-500/5 p-4">
 <div className="flex items-center justify-between mb-3">
 <h4 className="text-lg font-semibold text-red-400 flex items-center gap-2">
 <span>⚠️</span>
 Common Mistakes to Avoid
 </h4>
 {!isSpeaking && (
 <button
 onClick={() => speakText(safeToString(explanation.explanation.commonMistakes))}
 className="text-red-400 hover:text-red-300 p-1.5 rounded hover:bg-red-400/10"
 title="Listen to this section"
 >
 <SpeakerWaveIcon className="h-5 w-5" />
 </button>
 )}
 </div>
 <p className="text-sm text-muted leading-relaxed whitespace-pre-line">
 {safeToString(explanation.explanation.commonMistakes)}
 </p>
 </div>
 )}

 {/* Best Practices */}
 {explanation.explanation.bestPractices && (
 <div className="rounded-xl border border-green-500/20 bg-green-500/5 p-4">
 <div className="flex items-center justify-between mb-3">
 <h4 className="text-lg font-semibold text-green-400 flex items-center gap-2">
 <span>✅</span>
 Best Practices
 </h4>
 {!isSpeaking && (
 <button
 onClick={() => speakText(safeToString(explanation.explanation.bestPractices))}
 className="text-green-400 hover:text-green-300 p-1.5 rounded hover:bg-green-400/10"
 title="Listen to this section"
 >
 <SpeakerWaveIcon className="h-5 w-5" />
 </button>
 )}
 </div>
 <p className="text-sm text-muted leading-relaxed whitespace-pre-line">
 {safeToString(explanation.explanation.bestPractices)}
 </p>
 </div>
 )}
 </div>
 )}

 {/* Fallback content when no explanation is generated */}
 {!explanation.explanation && !explanation.loading && (
 <div className="space-y-4">
 <div className="rounded-xl border border-secondary/20 bg-bgMain/40 p-4">
 <div className="text-center py-8 space-y-4">
 <BookOpenIcon className="h-12 w-12 text-primary mx-auto mb-4" />
 <h4 className="text-lg font-semibold text-primary mb-2">Ready to Learn?</h4>
 <p className="text-sm text-muted mb-4">
 Generate a comprehensive AI-powered explanation of <strong>{task.topic}</strong> 
 tailored specifically for your experience level and course.
 </p>
 
 {/* Language selector removed - always English */}
 </div>

 <button
 onClick={explanation.onGenerate}
 className="rounded-xl bg-primary text-white px-6 py-3 text-sm font-semibold hover:bg-primary flex items-center gap-2 mx-auto"
 >
 <BookOpenIcon className="h-5 w-5" />
 Generate Topic Explanation
 </button>
 </div>

 {/* Basic topic information as fallback */}
 <div className="rounded-xl border border-secondary/20 bg-bgMain/40 p-4">
 <h4 className="text-lg font-semibold text-primary mb-3 flex items-center gap-2">
 <span>📖</span>
 Basic Topic Information
 </h4>
 <div className="space-y-3">
 {task.description && (
 <div>
 <h5 className="text-sm font-medium text-gray-200 mb-2">Description</h5>
 <p className="text-sm text-muted leading-relaxed">{task.description}</p>
 </div>
 )}
 
 {task.useCase && (
 <div>
 <h5 className="text-sm font-medium text-gray-200 mb-2">Use Case</h5>
 <p className="text-sm text-muted leading-relaxed">{task.useCase}</p>
 </div>
 )}
 
 {task.expectedOutput && (
 <div>
 <h5 className="text-sm font-medium text-gray-200 mb-2">Expected Output</h5>
 <p className="text-sm text-muted leading-relaxed">{task.expectedOutput}</p>
 </div>
 )}

 {task.subtopics && task.subtopics.length > 0 && (
 <div>
 <h5 className="text-sm font-medium text-gray-200 mb-2">Subtopics ({task.subtopics.length})</h5>
 <ul className="list-disc pl-5 space-y-1 text-sm text-muted">
 {task.subtopics.slice(0, 10).map((subtopic, index) => (
 <li key={index}>{subtopic}</li>
 ))}
 {task.subtopics.length > 10 && (
 <li className="text-primary">... and {task.subtopics.length - 10} more subtopics</li>
 )}
 </ul>
 </div>
 )}
 </div>
 </div>
 </div>
 )}
 </div>
 )}

 {tab ==="video" && (
 <div className="space-y-6">
 <div className="rounded-xl border border-secondary/20 bg-bgMain/40 p-6">
 <div className="flex items-center gap-3 mb-4">
 <VideoCameraIcon className="h-6 w-6 text-primary" />
 <h4 className="text-lg font-semibold text-primary">🎬 AI Tutorial Video Generator</h4>
 </div>
 
 <div className="space-y-4">
 {/* Language selector removed - always English */}
 <div className="bg-primary/10 border border-secondary/30 rounded-lg p-4">
 <div className="flex items-start gap-3">
 <div className="bg-secondary/20 p-2 rounded-lg">
 <VideoCameraIcon className="h-5 w-5 text-primary" />
 </div>
 <div className="flex-1">
 <h5 className="font-semibold text-primary mb-1">What You'll Get</h5>
 <p className="text-sm text-muted mb-2">
 A comprehensive tutorial video for <strong className="text-primary">{task.topic}</strong>
 </p>
 <div className="grid grid-cols-2 gap-2 text-xs text-muted">
 <div className="flex items-center gap-1">
 <span className="w-2 h-2 bg-primary rounded-full"></span>
 Duration: 60-90 seconds
 </div>
 <div className="flex items-center gap-1">
 <span className="w-2 h-2 bg-primary rounded-full"></span>
 HD Quality (1280x720)
 </div>
 <div className="flex items-center gap-1">
 <span className="w-2 h-2 bg-primary rounded-full"></span>
 {task.subtopics?.length || 0} subtopics covered
 </div>
 <div className="flex items-center gap-1">
 <span className="w-2 h-2 bg-primary rounded-full"></span>
 Code examples included
 </div>
 </div>
 </div>
 </div>
 </div>
 
 {!video?.url && video?.status !=="queued" ? (
 <div className="space-y-4">
 <button
 onClick={video?.onGenerate}
 disabled={video?.generating}
 className="w-full bg-primary hover:bg-secondary disabled:bg-primary/50 text-white font-bold py-4 px-6 rounded-xl transition-all duration-200 flex items-center justify-center gap-3 shadow-lg hover:shadow-xl disabled:shadow-none"
 >
 {video?.generating ? (
 <>
 <div className="animate-spin rounded-full h-6 w-6 border-2 border-black border-t-transparent"></div>
 <span>Generating Your Tutorial...</span>
 </>
 ) : (
 <>
 <VideoCameraIcon className="h-6 w-6" />
 <span>Generate Tutorial Video {videoUsage ? ` (${videoUsage.videos_remaining} left)` : ''}</span>
 </>
 )}
 </button>
 
 {video?.generating && (
 <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
 <div className="flex items-center gap-3 mb-3">
 <div className="animate-pulse bg-blue-500 w-3 h-3 rounded-full"></div>
 <span className="font-semibold text-blue-400">Creating Your Tutorial Video</span>
 </div>
 <div className="space-y-2 text-sm text-muted">
 <div className="flex justify-between">
 <span>Topic:</span>
 <span className="text-primary font-medium">{task.topic}</span>
 </div>
 <div className="flex justify-between">
 <span>Estimated Time:</span>
 <span className="text-green-400">1-2 minutes</span>
 </div>
 <div className="flex justify-between">
 <span>Content:</span>
 <span className="text-blue-400">{task.subtopics?.length || 0} subtopics + examples</span>
 </div>
 </div>
 <div className="mt-3 text-xs text-muted">
 💡 Please don't close this window while generating...
 </div>
 </div>
 )}
 
 {!video?.generating && (
 <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
 <div className="flex items-center gap-2 text-muted">
 <CheckCircleIcon className="h-4 w-4 text-green-400" />
 <span>Professional narration</span>
 </div>
 <div className="flex items-center gap-2 text-muted">
 <CheckCircleIcon className="h-4 w-4 text-green-400" />
 <span>Visual slides & code</span>
 </div>
 <div className="flex items-center gap-2 text-muted">
 <CheckCircleIcon className="h-4 w-4 text-green-400" />
 <span>Real-world examples</span>
 </div>
 <div className="flex items-center gap-2 text-muted">
 <CheckCircleIcon className="h-4 w-4 text-green-400" />
 <span>Best practices</span>
 </div>
 </div>
 )}
 </div>
 ) : (
 <div className="space-y-4">
 <div className="bg-gradient-to-r from-green-500/10 to-emerald-500/10 border border-green-500/30 rounded-lg p-4">
 <div className="flex items-center gap-3 mb-2">
 <div className="bg-green-500/20 p-2 rounded-lg">
 <CheckCircleIcon className="h-5 w-5 text-green-400" />
 </div>
 <div>
 <h5 className="font-semibold text-green-400">
 {video?.status ==="ready" ?"🎉 Video Generated Successfully!" :"⏳ Video rendering in progress"}
 </h5>
 <p className="text-sm text-muted">
 {video?.status ==="ready"
 ?"Your tutorial video is ready to watch"
 : video?.message ||"We're refreshing the player automatically until the MP4 is ready."}
 </p>
 </div>
 </div>
 </div>
 
 <div className="space-y-3">
 <div className="relative bg-bgMain rounded-lg overflow-hidden shadow-lg">
 {video?.url ? (
 <>
 <video
 controls
 className="w-full rounded-lg"
 style={{ maxHeight: '400px' }}
 poster="data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTI4MCIgaGVpZ2h0PSI3MjAiIHZpZXdCb3g9IjAgMCAxMjgwIDcyMCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjEyODAiIGhlaWdodD0iNzIwIiBmaWxsPSIjMUYyOTM3Ii8+CjxjaXJjbGUgY3g9IjY0MCIgY3k9IjM2MCIgcj0iNDAiIGZpbGw9IiNGQkJGMjQiLz4KPHBhdGggZD0iTTYyMCAzNDBMMjYwIDM0MEwyNjAgMzgwTDYyMCAzODBMNjIwIDM0MFoiIGZpbGw9IiMxRjI5MzciLz4KPC9zdmc+"
 onCanPlay={video?.onLoaded}
 onLoadedData={video?.onLoaded}
 onError={video?.onError}
 >
 <source src={getProxiedVideoUrl(video.url)} type="video/mp4" />
 Your browser does not support the video tag.
 </video>
 {video?.status !=="ready" && (
 <div className="absolute inset-0 bg-bgMain/70 flex flex-col items-center justify-center text-center p-4">
 <ArrowPathIcon className="h-8 w-8 text-primary animate-spin mb-3" />
 <p className="text-sm text-gray-200">
 {video?.message ||"Waiting for the video to finish rendering…"}
 </p>
 </div>
 )}
 </>
 ) : (
 <div className="p-6 text-center">
 {video?.status ==="queued" || video?.status ==="generating" ? (
 <div className="flex flex-col items-center justify-center gap-3">
 <ArrowPathIcon className="h-8 w-8 text-primary animate-spin" />
 <p className="text-sm text-gray-200 font-medium">
 {video?.message ||"Rendering video… This usually takes 1-3 minutes."}
 </p>
 <p className="text-xs text-muted">
 We'll update automatically once the video is ready.
 </p>
 </div>
 ) : (
 <div className="text-sm text-muted">
 Video URL not available yet. Generate a video to get started.
 </div>
 )}
 </div>
 )}
 </div>
 {video?.error && (
 <div className="bg-red-500/10 border border-red-500/30 text-red-200 text-sm rounded-lg p-3">
 {video.error}
 </div>
 )}
 
 <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
 <button
 onClick={() => window.open(video?.url, '_blank')}
 disabled={video?.status !=="ready"}
 className="bg-blue-500 hover:bg-blue-600 text-white font-semibold py-3 px-4 rounded-lg transition-colors flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
 title={video?.status ==="ready" ?"Open video in a new tab" :"Video still rendering"}
 >
 <ArrowTopRightOnSquareIcon className="h-5 w-5" />
 Open in New Tab
 </button>
 <button
 onClick={async () => {
 try {
 const response = await fetch(video?.url);
 const blob = await response.blob();
 const url = window.URL.createObjectURL(blob);
 const link = document.createElement('a');
 link.href = url;
 link.download = `tutorial-${task.topic.replace(/[^a-zA-Z0-9]/g, '-')}.mp4`;
 document.body.appendChild(link);
 link.click();
 document.body.removeChild(link);
 window.URL.revokeObjectURL(url);
 } catch (err) {
 console.error('Download failed:', err);
 alert('Download failed. Please try opening in a new tab instead.');
 }
 }}
 disabled={video?.status !=="ready"}
 className="bg-green-500 hover:bg-green-600 text-white font-semibold py-3 px-4 rounded-lg transition-colors flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
 title={video?.status ==="ready" ?"Download MP4" :"Video still rendering"}
 >
 <ArrowDownTrayIcon className="h-5 w-5" />
 Download Video
 </button>
 </div>
 
 <button
 onClick={video?.onGenerate}
 className="w-full bg-gray-600 hover:bg-gray-700 text-white font-semibold py-2 px-4 rounded-lg transition-colors flex items-center justify-center gap-2"
 >
 <ArrowPathIcon className="h-4 w-4" />
 Regenerate Video
 </button>
 </div>
 </div>
 )}
 </div>
 </div>
 </div>
 )}

 {tab === "submit" && (
      <div className="space-y-4">
        {/* Provisioned Repo Notice */}
        {githubRepoUrl && (
          <div className="rounded-xl border border-primary/40 bg-primary/10 p-4 mb-4">
            <h4 className="text-sm font-semibold text-primary mb-1 flex items-center gap-2">
              <CheckCircleIcon className="h-4 w-4" />
              Your Dedicated Project Repository
            </h4>
            <p className="text-xs text-muted mb-2">
              We've created a private GitHub repository for your project. Please push your code here.
            </p>
            <div className="flex items-center gap-3">
              <code className="bg-bgMain/60 px-2 py-1 rounded text-xs text-primary border border-primary/20 flex-1 truncate">
                {githubRepoUrl}
              </code>
              <a 
                href={githubRepoUrl} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-xs bg-primary text-white px-3 py-1 rounded hover:bg-secondary transition"
              >
                Open Repo
              </a>
            </div>
            {invitationStatus === 'SENT' && (
              <p className="text-[10px] text-orange-400 mt-2 italic">
                ⚠️ Check your email to accept the GitHub invitation if you haven't yet.
              </p>
            )}
          </div>
        )}

        {/* Submission Type Selection (Only if no provisioned repo) */}
        {!githubRepoUrl && (
          <div className="rounded-xl border border-secondary/20 bg-bgMain/40 p-4 mb-4">
            <h4 className="text-lg font-semibold text-primary mb-3 flex items-center gap-2">
              <CheckCircleIcon className="h-5 w-5" />
              Choose Submission Type
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <button
                onClick={() => setSubmissionType("github")}
                className={`p-4 rounded-lg border transition-all ${
                  submissionType === "github" 
                  ? "border-primary bg-primary/10" 
                  : "border-secondary/30 hover:border-primary/50"
                }`}
              >
                <div className="text-center">
                  <div className="text-2xl mb-2">🐙</div>
                  <h5 className="font-semibold text-primary mb-1">GitHub Repository</h5>
                  <p className="text-xs text-muted">Submit your code repository</p>
                </div>
              </button>
              <button
                onClick={() => setSubmissionType("files")}
                className={`p-4 rounded-lg border transition-all ${
                  submissionType === "files" 
                  ? "border-primary bg-primary/10" 
                  : "border-secondary/30 hover:border-primary/50"
                }`}
              >
                <div className="text-center">
                  <div className="text-2xl mb-2">📄</div>
                  <h5 className="font-semibold text-primary mb-1">Document + Image</h5>
                  <p className="text-xs text-muted">Upload documentation and screenshots</p>
                </div>
              </button>
            </div>
          </div>
        )}

        {/* File Upload Section */}
        {!githubRepoUrl && submissionType === "files" && (
          <div className="space-y-4">
            <p className="text-sm text-muted">Upload your <b className="text-primary">document</b> and an <b className="text-primary">image</b>.</p>
            <div className="grid sm:grid-cols-2 gap-4">
              <Uploader label="Document" accept=".pdf,.doc,.docx,.md,.txt" file={docFile} setFile={setDocFile}/>
              <Uploader label="Image" accept="image/*" file={imgFile} setFile={setImgFile}/>
            </div>
          </div>
        )}

        {/* GitHub URL Section */}
        {submissionType === "github" && (
          <div className="space-y-4">
            <p className="text-sm text-muted">
              {githubRepoUrl 
                ? "We'll review the code in your dedicated repository." 
                : "Provide your GitHub repository URL for code review."
              }
            </p>
            {!githubRepoUrl && (
              <label className="block">
                <span className="text-xs text-muted">GitHub Repository URL</span>
                <input 
                  type="url" 
                  className="mt-1 w-full rounded-lg bg-bgMain/40 border border-zinc-700 text-sm text-gray-200 p-3 focus:outline-none focus:border-secondary" 
                  placeholder="https://github.com/username/repository" 
                  value={githubUrl} 
                  onChange={(e)=>setGithubUrl(e.target.value)} 
                  disabled={submitting}
                />
              </label>
            )}
          </div>
        )}
 <label className="block">
 <span className="text-xs text-muted">Notes for reviewer (optional)</span>
 <textarea className="mt-1 w-full rounded-lg bg-bgMain/40 border border-zinc-700 text-sm text-gray-200 p-3 focus:outline-none focus:border-secondary" rows={3} placeholder="Anything you'd like to highlight or explain…" value={comments} onChange={(e)=>setComments(e.target.value)} disabled={submitting}/>
 </label>
 {serverError && <div className="text-xs text-red-400 flex items-center gap-2"><ExclamationTriangleIcon className="h-4 w-4"/> {serverError}</div>}
 <div className="flex justify-end">
 <button
 onClick={()=>onSubmit({ docFile, imgFile, comments, githubUrl, submissionType, setSubmitting, setServerError })}
 disabled={submitting||!githubUrl}
 className="rounded-xl bg-primary text-white px-4 py-2 text-sm font-semibold hover:bg-primary disabled:opacity-60 flex items-center gap-2"
 >
 {submitting ? <ArrowPathIcon className="h-4 w-4 animate-spin" /> : <CheckCircleIcon className="h-4 w-4" />}
 {submitting ?"Submitting…" :"Submit for AI review"}
 </button>
 </div>
 </div>
 )}

 {tab ==="feedback" && (
 !reviewState ? (
 <p className="text-sm text-muted">No submission yet. Use the “Submit” tab.</p>
 ) : reviewState.status ==="pending" ? (
 <p className="text-sm text-primary flex items-center gap-2"><ArrowPathIcon className="h-4 w-4 animate-spin"/> Submission received — awaiting AI review…</p>
 ) : reviewState.status ==="passed" ? (
 <div className="space-y-3">
 <div className="rounded-xl border border-green-500/40 bg-green-500/10 p-4">
 <div className="font-semibold text-green-400 flex items-center gap-2"><CheckCircleIcon className="h-5 w-5" />Passed</div>
 {"score" in reviewState && reviewState.score != null && <div className="text-xs text-muted mt-1">Score: {(reviewState.score*100).toFixed(0)}%</div>}
 {reviewState.details?.coverage?.length ? (
 <div className="mt-2 text-xs text-gray-200">
 <div className="text-muted mb-1">Subtopic coverage:</div>
 <ul className="list-disc pl-5 space-y-1">
 {reviewState.details.coverage.map((c,i)=>(<li key={i}>{c.subtopic} — <span className={c.hit ?"text-green-400":"text-red-400"}>{c.hit ?"addressed":"missing"}</span></li>))}
 </ul>
 </div>
 ) : null}
 {reviewState.feedback && <p className="text-sm text-gray-200 mt-2 whitespace-pre-line">{reviewState.feedback}</p>}
 </div>
 </div>
 ) : (
 <div className="rounded-xl border border-red-500/40 bg-red-500/10 p-4">
 <div className="font-semibold text-red-400 flex items-center gap-2"><ExclamationTriangleIcon className="h-5 w-5" />Changes needed</div>
 {reviewState.details?.coverage?.length ? (
 <div className="mt-2 text-xs text-gray-200">
 <div className="text-muted mb-1">Subtopic coverage:</div>
 <ul className="list-disc pl-5 space-y-1">
 {reviewState.details.coverage.map((c,i)=>(<li key={i}>{c.subtopic} — <span className={c.hit ?"text-green-400":"text-red-400"}>{c.hit ?"addressed":"missing"}</span></li>))}
 </ul>
 </div>
 ) : null}
 {reviewState.feedback && <p className="text-sm text-gray-200 mt-2 whitespace-pre-line">{reviewState.feedback}</p>}
 <div className="text-xs text-muted mt-2">Update your document/image and resubmit from the “Submit” tab.</div>
 </div>
 )
 )}

 {tab ==="visualize" && visualize?.show && (
 <div className="space-y-6">
 {/* Header Section */}
 <div className="rounded-xl border border-secondary/20 bg-bgMain/40 p-4">
 <h4 className="text-lg font-semibold text-primary mb-3 flex items-center gap-2">
 <PhotoIcon className="h-5 w-5" />
 AI-Powered Visualization Generator
 </h4>
 <p className="text-sm text-muted leading-relaxed">
 Generate <b className="text-primary">task-specific visualizations</b> including architecture diagrams, 
 dashboards, flowcharts, and mockups to help you understand how real-world implementations look.
 </p>
 </div>

 {/* Visualization Options */}
 <div className="rounded-xl border border-secondary/20 bg-bgMain/40 p-4">
 <h5 className="text-sm font-semibold text-gray-200 mb-3">Visualization Types</h5>
 <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
 {[
 { type:"architecture", label:"Architecture Diagram", icon:"🏗️" },
 { type:"dashboard", label:"Dashboard Mockup", icon:"📊" },
 { type:"flowchart", label:"Process Flow", icon:"🔄" },
 { type:"ui", label:"UI/UX Design", icon:"🎨" }
 ].map((option) => (
 <button
 key={option.type}
 className="p-3 rounded-lg border border-secondary/20 bg-primary/5 hover:bg-primary/10 hover:border-primary/40 transition text-center"
 onClick={() => visualize.setVisualHint(`${option.label}: ${visualize.visualHint}`)}
 >
 <div className="text-2xl mb-1">{option.icon}</div>
 <div className="text-xs text-gray-200">{option.label}</div>
 </button>
 ))}
 </div>
 </div>

 {/* Input Section */}
 <div className="rounded-xl border border-secondary/20 bg-bgMain/40 p-4">
 <label className="block">
 <span className="text-sm font-medium text-gray-200 mb-2 block">
 Custom Visualization Prompt
 </span>
 <textarea
 rows={4}
 className="w-full rounded-lg bg-bgMain/40 border border-zinc-700 text-sm text-gray-200 p-3 focus:outline-none focus:border-secondary resize-none"
 value={visualize.visualHint}
 onChange={(e)=>visualize.setVisualHint(e.target.value)}
 placeholder="Describe what you want to visualize... e.g., 'AWS architecture with VPC, subnets, load balancer, RDS, and S3 bucket' or 'React dashboard with charts, tables, and navigation'"
 />
 </label>
 
 <div className="mt-3 flex items-center justify-between">
 <div className="text-xs text-muted">
 💡 Be specific about components, layout, and style for better results
 </div>
 <button
 onClick={visualize.onVisualize}
 disabled={visualize.visualizing}
 className="rounded-xl bg-primary text-white px-6 py-3 text-sm font-semibold hover:bg-primary disabled:opacity-60 flex items-center gap-2"
 >
 {visualize.visualizing ? (
 <>
 <ArrowPathIcon className="h-4 w-4 animate-spin" />
 Generating...
 </>
 ) : (
 <>
 <PhotoIcon className="h-4 w-4" />
 Generate Visualization
 </>
 )}
 </button>
 </div>
 </div>

 <VisualizationPreview
 loading={visualize.visualizing}
 imageUrl={visualize.visualUrl}
 taskTitle={task?.title || task?.topic}
 hint={visualize.visualHint}
 generatedAt={visualize.visualGeneratedAt}
 downloadFileName={`${slugify(task?.title || task?.topic || 'visual')}-visual.png`}
 />

 {/* Tips Section */}
 <div className="rounded-xl border border-blue-500/20 bg-blue-500/5 p-4">
 <h5 className="text-sm font-semibold text-blue-400 mb-2 flex items-center gap-2">
 <span>💡</span>
 Pro Tips for Better Visualizations
 </h5>
 <ul className="text-xs text-muted space-y-1 list-disc pl-4">
 <li>Be specific about technologies, frameworks, and components</li>
 <li>Include layout preferences (e.g.,"side-by-side","top-down")</li>
 <li>Mention color schemes or design styles if important</li>
 <li>Specify the level of detail (high-level overview vs detailed components)</li>
 <li>Include any specific tools or platforms (AWS, React, etc.)</li>
 </ul>
 </div>
 </div>
 )}
 </div>
 </div>
 </div>
 );
}

function Uploader({ label, accept, file, setFile }) {
 const fileInputRef = useRef(null);
 
 const handleFileSelect = (e) => {
 setFile(e.target.files?.[0] || null);
 };

 return (
 <div className="block">
 <span className="text-xs text-muted">{label}</span>
 <div className="mt-1 flex items-center gap-2">
 <input 
 ref={fileInputRef}
 type="file" 
 accept={accept} 
 onChange={handleFileSelect} 
 className="hidden"
 />
 <button
 type="button"
 onClick={() => fileInputRef.current?.click()}
 className="bg-primary text-white px-3 py-2 rounded-lg text-sm font-semibold hover:bg-primary transition-colors"
 >
 Choose File
 </button>
 {file ? (
 <span className="text-xs text-muted truncate max-w-[12rem]">{file.name}</span>
 ) : (
 <span className="text-xs text-muted">No file chosen</span>
 )}
 </div>
 </div>
 );
}
function MentorModal({ onClose, messages, setMessages, onSend, technology, task }) {
  const [text, setText] = useState("");
  const [imgFile, setImgFile] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [sending, setSending] = useState(false);
  const [quickQuestions] = useState([
    `Explain ${technology} in simple terms`,
    `What are the best practices for ${technology}?`,
    `Show me a ${technology} code example`,
    "What are common mistakes to avoid?",
    "How does this relate to real-world projects?"
  ]);
  const listRef = useRef(null);
  useEffect(() => { if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight; }, [messages]);

  const send = async () => {
    const t = text.trim();
    if (!t && !imgFile) return;
    setSending(true);
    setText("");
    const f = imgFile;
    setImgFile(null);
    await onSend({ text: t, imgFile: f });
    setSending(false);
  };

  const sendQuickQuestion = async (question) => {
    setSending(true);
    await onSend({ text: question, imgFile: null });
    setSending(false);
  };

  const clearChat = () => {
    setMessages([]);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
      <div className={`w-full bg-[#f0f9ff] border border-sky-200 rounded-2xl shadow-xl overflow-hidden ${isFullscreen ? 'h-full max-w-none' : 'max-w-5xl'}`}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-sky-100">
          <div className="flex items-center gap-2">
            <ChatBubbleLeftRightIcon className="h-5 w-5 text-black" />
            <h3 className="font-bold text-black">AI Mentor • {technology}</h3>
          </div>
          <div className="flex items-center gap-2">
            {task?.title && (
              <span className="text-xs text-black bg-bgSection px-2 py-1 rounded">
                {task.title}
              </span>
            )}
            <button 
              onClick={clearChat}
              className="text-xs text-black hover:text-primary px-2 py-1 rounded hover:bg-bgSection"
              title="Clear chat history"
            >
              Clear
            </button>
            <button
              onClick={() => setIsFullscreen(!isFullscreen)}
              className="text-black hover:text-primary p-1 rounded hover:bg-bgSection"
              title={isFullscreen ? "Exit Fullscreen" : "Enter Fullscreen"}
            >
              {isFullscreen ? (
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              ) : (
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                </svg>
              )}
            </button>
            <button className="p-1 rounded hover:bg-zinc-800" onClick={onClose}>
              <XMarkIcon className="h-5 w-5 text-black"/>
            </button>
          </div>
        </div>
 
 <div className={`grid grid-rows-[1fr_auto] ${isFullscreen ? 'h-[calc(100vh-80px)]' : 'h-[75vh]'}`}>
 <div ref={listRef} className="px-5 py-4 overflow-y-auto">
 {messages.length === 0 && (
 <div className="space-y-4">
 <div className="text-center py-8">
 <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
 <ChatBubbleLeftRightIcon className="h-8 w-8 text-primary"/>
 </div>
 <h4 className="text-lg font-semibold text-primary mb-2">Your AI Learning Assistant</h4>
 <p className="text-sm text-black mb-6">
 I'm here to help you with <span className="text-primary">{technology}</span> concepts, 
 {task?.title ? <> especially with <strong className="text-black">"{task.title}"</strong></> : null}. 
 Ask me anything or use the quick questions below!
 </p>
 </div>

 {/* Quick Questions */}
 <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
 {quickQuestions.map((question, index) => (
 <button
 key={index}
 onClick={() => sendQuickQuestion(question)}
 disabled={sending}
 className="text-left p-3 rounded-lg border border-secondary/20 bg-primary/5 hover:bg-primary/10 hover:border-primary/40 transition text-sm text-black disabled:opacity-50"
 >
 {question}
 </button>
 ))}
 </div>
 </div>
 )}
 
 <ul className="space-y-4">
 {messages.map((m)=>(
 <li key={m.id} className={`flex ${m.role==="user" ?"justify-end" :"justify-start"}`}>
 <div className={`max-w-[85%] rounded-xl px-4 py-3 text-sm whitespace-pre-line ${
 m.role==="user" 
 ?"bg-primary text-white" 
 :"bg-white border border-sky-200 text-black shadow-sm"
 }`}>
 {m.typing && (
 <div className="flex items-center gap-2 text-primary">
 <div className="flex space-x-1">
 <div className="w-2 h-2 bg-primary rounded-full animate-bounce"></div>
 <div className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{animationDelay: '0.1s'}}></div>
 <div className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></div>
 </div>
 <span className="text-xs">AI is thinking...</span>
 </div>
 )}
 {!m.typing && m.text}
 {m.imagePreview && <img src={m.imagePreview} alt="" className="mt-2 rounded-lg max-h-56" />}
 {m.when && (
 <div className="text-xs opacity-60 mt-1">
 {new Date(m.when).toLocaleTimeString()}
 </div>
 )}
 </div>
 </li>
 ))}
 </ul>
 </div>
 
 <div className="border-t border-sky-100 p-4">
 <div className="flex items-end gap-3">
 <div className="flex-1">
 <textarea 
 rows={3} 
 className="w-full rounded-lg bg-white border border-sky-200 text-sm text-black p-3 focus:outline-none focus:border-primary resize-none" 
 placeholder={`Ask me anything about ${technology}${task?.title ? ` or"${task.title}"` : ''}...`} 
 value={text} 
 onChange={(e)=>setText(e.target.value)}
 onKeyDown={(e) => {
 if (e.key === 'Enter' && !e.shiftKey) {
 e.preventDefault();
 send();
 }
 }}
 disabled={sending}
 />
 <div className="flex items-center justify-between mt-2">
 <label className="text-xs text-black flex items-center gap-2 cursor-pointer">
 <PaperClipIcon className="h-4 w-4 text-black" /> 
 {imgFile ? (
 <span className="text-primary truncate max-w-[12rem]">{imgFile.name}</span>
 ) : (
 <span className="text-black">Attach screenshot or code</span>
 )}
 <input 
 type="file" 
 accept="image/*,.txt,.js,.py,.java,.cpp,.c,.html,.css,.json" 
 className="hidden" 
 onChange={(e)=>setImgFile(e.target.files?.[0] || null)} 
 />
 </label>
 <div className="text-[11px] text-black">
 Press Enter to send, Shift+Enter for new line
 </div>
 </div>
 </div>
 <button 
 onClick={send} 
 disabled={sending || (!text.trim() && !imgFile)}
 className="rounded-xl bg-primary text-white px-6 py-3 text-sm font-semibold hover:bg-primary disabled:opacity-50 flex items-center gap-2"
 >
 {sending ? (
 <>
 <ArrowPathIcon className="h-4 w-4 animate-spin" />
 Sending...
 </>
 ) : (
 <>
 <PaperAirplaneIcon className="h-4 w-4" />
 Send
 </>
 )}
 </button>
  )}
 </div>
 </div>
 </div>
 </div>
 </div>
);
}



